import type { Bi, SkillId } from '@/sanskrit/lib/sanskrit/types'
import type {
    Beat,
    BoardLine,
    ChoiceOption,
    CurriculumLevel,
    Lesson,
    Step,
    Tone
} from '@/sanskrit/lib/content/lesson-types'

const bi = (hi: string, en: string): Bi => ({ hi, en })

/** A board line written as plain text, or with |tone| markers: 'पठ|saffron:ा|terracotta:मि' */
const line = (text: string, size: BoardLine['size'] = 'lg', caption?: Bi): BoardLine => ({
    segs: text.split('|').map(part => {
        const [tone, t] = part.includes(':') && /^(chalk|saffron|terracotta|green|muted):/.test(part) ? part.split(/:([\s\S]*)/) : [undefined, part]
        return { t: t ?? part, tone: tone as Tone | undefined }
    }),
    size,
    caption
})

const beat = (say: Bi, board?: BoardLine[], speak?: string, extra: Partial<Beat> = {}): Beat => ({ say, board, speak, ...extra })

const mcq = (q: Bi, options: ChoiceOption[], answer: number, explain: Bi, skill: SkillId, extra: { sa?: string; item?: string; playAudio?: boolean } = {}): Step => ({ kind: 'mcq', q, options, answer, explain, skill, ...extra })

const fill = (q: Bi, before: string, after: string, options: string[], answer: string, explain: Bi, skill: SkillId, item?: string): Step => ({ kind: 'fill', q, before, after, options, answer, explain, skill, item })

const build = (prompt: Bi, tiles: string[], answer: string[], skill: SkillId = 'sentence', extra: { orderFree?: boolean; item?: string; hint?: Bi } = {}): Step => ({ kind: 'build', prompt, tiles, answer, skill, ...extra })

const listen = (sa: string, options: string[], skill: SkillId = 'listening', q: Bi = bi('आपने क्या सुना?', 'What did you hear?')): Step => ({ kind: 'listen', q, sa, options, answer: sa, skill })

const word = (sa: string, hi: string, en: string, extra: { note?: Bi; icon?: string; figure?: 'self' | 'you' | 'other' } = {}): Step => ({ kind: 'word', sa, meaning: bi(hi, en), ...extra })

export const LESSONS: Lesson[] = [
    {
        id: 'l01-intro',
        number: 1,
        level: 0,
        moduleId: 'm0-intro',
        title: bi('संस्कृत क्या है?', 'What is Sanskrit?'),
        sa: 'संस्कृतपरिचयः',
        summary: bi('संस्कृत भाषा, उसकी लिपि और इस यात्रा से परिचय।', 'Meet the language, its script and how this journey works.'),
        minutes: 4,
        skills: ['vocabulary', 'devanagari'],
        steps: [
            {
                kind: 'intro',
                art: 'guru',
                sa: 'संस्कृतम्',
                title: bi('संस्कृत में आपका स्वागत है', 'Welcome to Sanskrit'),
                body: bi(
                    'संस्कृत भारत की एक प्राचीन भाषा है। वेद, उपनिषद्, रामायण, महाभारत, भगवद्गीता और काव्य, गणित, आयुर्वेद, दर्शन के असंख्य ग्रन्थ इसी में लिखे गए। आज भी यह पढ़ी, लिखी और बोली जाती है।',
                    'Sanskrit is an ancient language of Bharat. The Vedas, the Upaniṣads, the Rāmāyaṇa, the Mahābhārata, the Bhagavad Gītā and countless works of poetry, mathematics, medicine and philosophy were written in it. It is still read, written and spoken today.'
                )
            },
            word('संस्कृतम्', 'संस्कृत भाषा', 'Sanskrit', {
                icon: 'languages',
                note: bi('"संस्कृत" का अर्थ है भली-भाँति बनाया हुआ, परिष्कृत: सम् + कृत।', '"Saṃskṛta" means "well made, refined": सम् (well, together) + कृत (made).')
            }),
            {
                kind: 'teach',
                title: bi('संस्कृत कैसे लिखी जाती है', 'How Sanskrit is written'),
                beats: [
                    beat(bi('संस्कृत ध्वनि के अनुसार लिखी जाती है: जो लिखा है, वही बोला जाता है।', 'Sanskrit is written exactly as it sounds: what you see is what you say.'), [line('संस्कृतम्', 'xl')], 'संस्कृतम्'),
                    beat(bi('आज संस्कृत प्रायः देवनागरी लिपि में लिखी जाती है, जिसमें हिन्दी और मराठी भी लिखी जाती हैं।', 'Today Sanskrit is usually written in the Devanagari script, which Hindi and Marathi also use.'), [line('देवनागरी', 'lg')], 'देवनागरी'),
                    beat(bi('दो हज़ार से भी अधिक वर्ष पहले महर्षि पाणिनि ने संस्कृत व्याकरण को लगभग चार हज़ार सूत्रों में बाँधा। उस ग्रन्थ का नाम है अष्टाध्यायी।', 'More than two thousand years ago the grammarian Pāṇini described Sanskrit in about four thousand short rules, in a work called the Aṣṭādhyāyī.'), [line('saffron:पाणिनिः', 'lg'), line('अष्टाध्यायी', 'md')], 'पाणिनिः'),
                    beat(bi('यहाँ हर संस्कृत शब्द को छूकर उसका अर्थ और व्याकरण देखा जा सकता है। आइए आरम्भ करें!', 'Here you can tap any Sanskrit word to see its meaning and grammar. Let us begin!'), [line('green:आरम्भः', 'lg', bi('आरम्भ', 'the beginning'))])
                ]
            },
            mcq(bi('आज संस्कृत प्रायः किस लिपि में लिखी जाती है?', 'Which script is Sanskrit usually written in today?'), [bi('देवनागरी', 'Devanagari'), bi('रोमन', 'Roman'), bi('अरबी', 'Arabic')], 0, bi('आज मुख्यतः देवनागरी, यद्यपि इतिहास में संस्कृत अनेक लिपियों में लिखी गई।', 'Mostly Devanagari today, though historically Sanskrit has been written in many scripts.'), 'devanagari'),
            mcq(bi('"संस्कृत" का अर्थ क्या है?', 'What does the word "Saṃskṛta" mean?'), [bi('परिष्कृत, भली-भाँति बनाया हुआ', 'refined, well made'), bi('पुराना', 'old'), bi('गाया हुआ', 'sung')], 0, bi('सम् + कृत = भली-भाँति बनाया हुआ।', 'सम् + कृत = well made, refined.'), 'vocabulary'),
            mcq(bi('संस्कृत व्याकरण के प्रसिद्ध सूत्रकार कौन हैं?', 'Who is the famous grammarian of Sanskrit?'), [bi('पाणिनि', 'Pāṇini'), bi('कालिदास', 'Kālidāsa'), bi('वाल्मीकि', 'Vālmīki')], 0, bi('पाणिनि ने अष्टाध्यायी की रचना की। कालिदास कवि थे, वाल्मीकि ने रामायण रची।', 'Pāṇini wrote the Aṣṭādhyāyī. Kālidāsa was a poet and Vālmīki composed the Rāmāyaṇa.'), 'vocabulary'),
            listen('नमस्ते', ['नमस्ते', 'स्वागतम्', 'धन्यवादः'], 'listening', bi('सुनिए और पहचानिए: पहला अभिवादन।', 'Listen and pick: your first greeting.'))
        ]
    },
    {
        id: 'l02-svara',
        number: 2,
        level: 0,
        moduleId: 'm0-lipi',
        track: 'devanagari',
        title: bi('स्वर', 'Vowels'),
        sa: 'स्वराः',
        summary: bi('ह्रस्व और दीर्घ स्वर, और संयुक्त स्वर।', 'Short and long vowels, and the diphthongs.'),
        minutes: 6,
        skills: ['devanagari', 'pronunciation', 'listening'],
        steps: [
            {
                kind: 'intro',
                art: 'guru',
                title: bi('स्वर: अपने आप बोले जाने वाले वर्ण', 'Vowels: sounds that stand on their own'),
                body: bi('संस्कृत वर्णमाला स्वरों से आरम्भ होती है। स्वर बिना किसी दूसरे वर्ण की सहायता के बोले जाते हैं। उन्हें छूकर सुनिए।', 'The Sanskrit alphabet begins with the vowels (स्वराः). They can be pronounced on their own. Tap each one to hear it.')
            },
            {
                kind: 'letters',
                title: bi('संस्कृत के स्वर', 'The Sanskrit vowels'),
                note: bi('ए, ऐ, ओ, औ संस्कृत में सदा दीर्घ होते हैं।', 'In Sanskrit ए, ऐ, ओ and औ are always long.'),
                groups: [
                    {
                        label: bi('ह्रस्व (एक मात्रा)', 'Short (one beat)'),
                        letters: [
                            { ch: 'अ', iast: 'a' },
                            { ch: 'इ', iast: 'i' },
                            { ch: 'उ', iast: 'u' },
                            { ch: 'ऋ', iast: 'ṛ', note: bi('मूलतः स्वर रूप र; आज प्रायः "रि" जैसा बोला जाता है।', 'Originally a vowel r; today often pronounced like "ri".') },
                            { ch: 'ऌ', iast: 'ḷ', note: bi('दुर्लभ; केवल क्लृप् धातु में।', 'Rare; found only in the root क्लृप्.') }
                        ]
                    },
                    {
                        label: bi('दीर्घ (दो मात्रा)', 'Long (two beats)'),
                        letters: [
                            { ch: 'आ', iast: 'ā' },
                            { ch: 'ई', iast: 'ī' },
                            { ch: 'ऊ', iast: 'ū' },
                            { ch: 'ॠ', iast: 'ṝ' }
                        ]
                    },
                    {
                        label: bi('संयुक्त स्वर (सदा दीर्घ)', 'Diphthongs (always long)'),
                        letters: [
                            { ch: 'ए', iast: 'e' },
                            { ch: 'ऐ', iast: 'ai' },
                            { ch: 'ओ', iast: 'o' },
                            { ch: 'औ', iast: 'au' }
                        ]
                    }
                ]
            },
            {
                kind: 'teach',
                title: bi('ह्रस्व और दीर्घ', 'Short and long'),
                beats: [
                    beat(bi('दीर्घ स्वर ह्रस्व से दुगुने समय तक बोला जाता है।', 'A long vowel is held about twice as long as a short one.'), [line('अ — |saffron:आ', 'xl')], 'अ आ'),
                    beat(bi('इ और ई, उ और ऊ: यही अन्तर अर्थ बदल सकता है।', 'इ and ई, उ and ऊ: this difference can change meaning.'), [line('इ — |saffron:ई', 'xl'), line('उ — |saffron:ऊ', 'xl')], 'इ ई उ ऊ'),
                    beat(bi('संस्कृत में स्वर की लम्बाई बहुत महत्त्वपूर्ण है: बालकः (एक लड़का) और बालकाः (कई लड़के) में केवल यही अन्तर है।', 'Vowel length matters a lot in Sanskrit: बालकः (one boy) and बालकाः (many boys) differ only in this.'), [line('बालकः', 'lg'), line('बाल|saffron:काः', 'lg')], 'बालकः बालकाः')
                ]
            },
            mcq(bi('इ का दीर्घ रूप कौन सा है?', 'Which is the long form of इ?'), ['ई', 'उ', 'ऐ'], 0, bi('इ (i) का दीर्घ ई (ī) है।', 'The long form of इ (i) is ई (ī).'), 'devanagari', { item: 'letter:ई' }),
            listen('आ', ['अ', 'आ', 'ओ'], 'listening'),
            {
                kind: 'match',
                title: bi('अक्षर और उच्चारण मिलाइए', 'Match each letter to its sound'),
                pairs: [
                    { left: 'अ', right: 'a' },
                    { left: 'आ', right: 'ā' },
                    { left: 'इ', right: 'i' },
                    { left: 'ई', right: 'ī' },
                    { left: 'उ', right: 'u' },
                    { left: 'ऊ', right: 'ū' }
                ],
                skill: 'devanagari'
            },
            mcq(bi('संस्कृत में ए और ओ हमेशा...', 'In Sanskrit, ए and ओ are always...'), [bi('दीर्घ', 'long'), bi('ह्रस्व', 'short'), bi('मौन', 'silent')], 0, bi('संस्कृत में ह्रस्व ए या ओ नहीं होते।', 'Sanskrit has no short e or o.'), 'devanagari'),
            fill(bi('वर्णमाला का क्रम पूरा कीजिए', 'Complete the order of the alphabet'), 'अ, आ, इ, ', ', उ, ऊ', ['ई', 'ए', 'ऋ'], 'ई', bi('क्रम: अ आ इ ई उ ऊ ऋ ॠ ऌ ए ऐ ओ औ।', 'The order is अ आ इ ई उ ऊ ऋ ॠ ऌ ए ऐ ओ औ.'), 'devanagari'),
            { kind: 'trace', title: bi('अक्षर लिखने का अभ्यास', 'Trace the letters'), letters: ['अ', 'आ', 'इ'] }
        ]
    },
    {
        id: 'l03-vyanjana',
        number: 3,
        level: 0,
        moduleId: 'm0-lipi',
        track: 'devanagari',
        title: bi('व्यञ्जन', 'Consonants'),
        sa: 'व्यञ्जनानि',
        summary: bi('मुख में उच्चारण-स्थान के अनुसार व्यवस्थित व्यञ्जन।', 'Consonants arranged by where the mouth makes them.'),
        minutes: 7,
        skills: ['devanagari', 'pronunciation', 'listening'],
        steps: [
            {
                kind: 'intro',
                title: bi('मुँह का नक्शा', 'A map of the mouth'),
                body: bi('संस्कृत व्यञ्जन एक वैज्ञानिक तालिका में रखे गए हैं: गले से होंठों तक, उच्चारण-स्थान के क्रम में। हर पंक्ति (वर्ग) में पाँच अक्षर हैं।', 'Sanskrit consonants sit in a scientific grid, ordered from the throat to the lips by where they are pronounced. Each row (वर्ग) has five letters.')
            },
            {
                kind: 'varga',
                title: bi('व्यञ्जन तालिका', 'The consonant grid'),
                note: bi('किसी भी अक्षर को छूकर उसका उच्चारण और स्थान देखिए।', 'Tap any letter to hear it and see where it is made.')
            },
            {
                kind: 'teach',
                title: bi('ध्यान से सुनने वाले अन्तर', 'Sounds to listen for carefully'),
                beats: [
                    beat(bi('अल्पप्राण और महाप्राण: क (ka) में हवा कम, ख (kha) में हवा का झोंका। मुँह के सामने हथेली रखकर देखिए।', 'Unaspirated and aspirated: क (ka) has little breath, ख (kha) has a puff of air. Try it with your palm in front of your mouth.'), [line('क — |saffron:ख', 'xl'), line('ग — |saffron:घ', 'xl')], 'क ख ग घ'),
                    beat(bi('दन्त्य और मूर्धन्य: त में जीभ दाँतों को छूती है, ट में जीभ ऊपर मुड़कर तालु को छूती है।', 'Dental and retroflex: for त the tongue touches the teeth; for ट it curls back to the roof of the mouth.'), [line('त — |terracotta:ट', 'xl'), line('द — |terracotta:ड', 'xl')], 'त ट द ड'),
                    beat(bi('तीन स, तीन स्थान: श (तालु), ष (मूर्धा, जीभ मुड़ी हुई), स (दाँत)।', 'Three s-sounds from three places: श (palate), ष (retroflex), स (teeth).'), [line('श  ष  स', 'xl')], 'श ष स'),
                    beat(bi('कुछ संयुक्ताक्षर अपने आकार से पहचाने जाते हैं: क्ष = क् + ष, त्र = त् + र, ज्ञ = ज् + ञ।', 'Some conjuncts have their own shapes: क्ष = क् + ष, त्र = त् + र, ज्ञ = ज् + ञ.'), [line('क्ष  त्र  ज्ञ', 'xl')], 'क्ष त्र ज्ञ')
                ]
            },
            mcq(bi('इनमें से महाप्राण कौन है?', 'Which of these is aspirated (महाप्राण)?'), ['ख', 'क', 'ग'], 0, bi('ख, घ, छ, झ... ये महाप्राण हैं: इनमें हवा का झोंका होता है।', 'ख, घ, छ, झ… are aspirated: they carry a puff of air.'), 'pronunciation'),
            mcq(bi('त बोलते समय जीभ कहाँ लगती है?', 'Where does the tongue touch when you say त?'), [bi('दाँतों पर (दन्त्य)', 'the teeth (dental)'), bi('ऊपर मुड़कर तालु पर (मूर्धन्य)', 'curled back to the roof (retroflex)'), bi('होंठों पर', 'the lips')], 0, bi('त, थ, द, ध, न दन्त्य हैं; ट, ठ, ड, ढ, ण मूर्धन्य।', 'त थ द ध न are dental; ट ठ ड ढ ण are retroflex.'), 'pronunciation'),
            listen('ख', ['क', 'ख', 'ग'], 'listening'),
            {
                kind: 'match',
                title: bi('कवर्ग: अक्षर और उच्चारण', 'The क row: letters and sounds'),
                pairs: [
                    { left: 'क', right: 'ka' },
                    { left: 'ख', right: 'kha' },
                    { left: 'ग', right: 'ga' },
                    { left: 'घ', right: 'gha' },
                    { left: 'ङ', right: 'ṅa' }
                ],
                skill: 'devanagari'
            },
            mcq(bi('प किस वर्ग का है?', 'Which row does प belong to?'), [bi('पवर्ग (होंठ)', 'the प row (lips)'), bi('कवर्ग (गला)', 'the क row (throat)'), bi('टवर्ग (मूर्धा)', 'the ट row (retroflex)')], 0, bi('प, फ, ब, भ, म होंठों से बोले जाते हैं: ओष्ठ्य।', 'प फ ब भ म are made with the lips: labials.'), 'devanagari'),
            { kind: 'trace', title: bi('लिखने का अभ्यास', 'Trace the letters'), letters: ['क', 'ग', 'म'] }
        ]
    },
    {
        id: 'l04-matra',
        number: 4,
        level: 0,
        moduleId: 'm0-lipi',
        track: 'devanagari',
        title: bi('मात्राएँ', 'Vowel signs'),
        sa: 'मात्राः',
        summary: bi('व्यञ्जन + स्वर-चिह्न = अक्षर: क + ा = का।', 'Consonant + vowel sign = syllable: क + ा = का.'),
        minutes: 6,
        skills: ['devanagari', 'reading'],
        steps: [
            {
                kind: 'intro',
                title: bi('हर व्यञ्जन में छिपा "अ"', 'The hidden अ in every consonant'),
                body: bi('क अकेला "क्" नहीं, "क" (ka) है: इसमें अ छिपा है। दूसरा स्वर जोड़ने के लिए हम मात्रा लगाते हैं।', 'The letter क is not a bare "k" but "ka": an अ is built in. To add any other vowel we attach a vowel sign (मात्रा).')
            },
            {
                kind: 'teach',
                title: bi('मात्रा कैसे जुड़ती है', 'How vowel signs attach'),
                beats: [
                    beat(bi('क् + अ = क। हलन्त (्) बताता है कि अ नहीं है।', 'क् + अ = क. The halant (्) shows there is no vowel.'), [line('क् + अ = |saffron:क', 'xl')], 'क'),
                    beat(bi('क् + आ = का। आ की मात्रा ा व्यञ्जन के बाद लगती है।', 'क् + आ = का. The sign ा comes after the consonant.'), [line('क् + आ = |saffron:का', 'xl')], 'का'),
                    beat(bi('क् + इ = कि। ध्यान दीजिए: ि की मात्रा पहले लिखी जाती है, पर बोली बाद में जाती है!', 'क् + इ = कि. Notice: the sign ि is written BEFORE the consonant but pronounced after it!'), [line('क् + इ = |saffron:कि', 'xl')], 'कि'),
                    beat(bi('की, कु, कू, कृ, के, कै, को, कौ: बाकी मात्राएँ।', 'की, कु, कू, कृ, के, कै, को, कौ: the remaining signs.'), [line('की  कु  कू  कृ', 'lg'), line('के  कै  को  कौ', 'lg')], 'की कु कू कृ के कै को कौ'),
                    beat(bi('र के साथ उ और ऊ का आकार अलग होता है: रु, रू।', 'With र the signs for उ and ऊ change shape: रु, रू.'), [line('र + उ = |saffron:रु', 'lg'), line('र + ऊ = |saffron:रू', 'lg')], 'रु रू')
                ]
            },
            {
                kind: 'matra',
                title: bi('मात्रा प्रयोगशाला', 'The vowel-sign builder'),
                consonants: ['क', 'ग', 'त', 'न', 'प', 'म', 'र', 'स'],
                vowels: ['अ', 'आ', 'इ', 'ई', 'उ', 'ऊ', 'ऋ', 'ए', 'ऐ', 'ओ', 'औ'],
                note: bi('एक व्यञ्जन और एक स्वर चुनिए।', 'Pick a consonant and a vowel.')
            },
            mcq(bi('क + ी = ?', 'क + ी = ?'), ['की', 'कि', 'कू'], 0, bi('ी दीर्घ ई की मात्रा है।', 'ी is the sign for long ई.'), 'devanagari'),
            mcq(bi('कौन सी मात्रा व्यञ्जन से पहले लिखी जाती है?', 'Which vowel sign is written before the consonant?'), ['ि', 'ी', 'ु'], 0, bi('ि (इ) व्यञ्जन के बाएँ लिखी जाती है।', 'ि (for इ) is written to the left of its consonant.'), 'devanagari'),
            {
                kind: 'match',
                title: bi('अक्षर और उच्चारण', 'Syllables and sounds'),
                pairs: [
                    { left: 'का', right: 'kā' },
                    { left: 'कि', right: 'ki' },
                    { left: 'कु', right: 'ku' },
                    { left: 'के', right: 'ke' },
                    { left: 'को', right: 'ko' }
                ],
                skill: 'devanagari'
            },
            listen('की', ['कि', 'की', 'कु'], 'listening'),
            fill(bi('प + ू = ?', 'प + ू = ?'), 'प + ू = ', '', ['पू', 'पु', 'पो'], 'पू', bi('ू दीर्घ ऊ की मात्रा है: पू (pū)।', 'ू is the sign for long ऊ: पू (pū).'), 'devanagari')
        ]
    },
    {
        id: 'l05-pathanam',
        number: 5,
        level: 0,
        moduleId: 'm0-lipi',
        track: 'devanagari',
        title: bi('शब्द पढ़ना', 'Reading words'),
        sa: 'शब्दपठनम्',
        summary: bi('अनुस्वार, विसर्ग, हलन्त और संयुक्ताक्षर के साथ पूरे शब्द पढ़ें।', 'Read whole words with anusvāra, visarga, halant and conjuncts.'),
        minutes: 7,
        skills: ['devanagari', 'reading'],
        steps: [
            {
                kind: 'intro',
                title: bi('चार नए चिह्न', 'Four new marks'),
                body: bi('अब असली शब्द पढ़ेंगे। इसके लिए चार चिह्न चाहिए: ं अनुस्वार, ः विसर्ग, ् हलन्त, और संयुक्ताक्षर।', 'Now we read real words. For that we need four marks: ं anusvāra, ः visarga, ् halant, and conjunct letters.')
            },
            {
                kind: 'teach',
                title: bi('चिह्न और उनकी ध्वनि', 'Marks and their sounds'),
                beats: [
                    beat(bi('ं अनुस्वार: स्वर के बाद नासिक्य ध्वनि। संस्कृतम् = saṃskṛtam।', 'ं anusvāra: a nasal sound after a vowel. संस्कृतम् = saṃskṛtam.'), [line('saffron:सं|स्कृतम्', 'xl')], 'संस्कृतम्'),
                    beat(bi('ः विसर्ग: स्वर के बाद हल्की "ह" ध्वनि। रामः = rāmaḥ।', 'ः visarga: a soft breathy "h" after a vowel. रामः = rāmaḥ.'), [line('रा|saffron:मः', 'xl')], 'रामः'),
                    beat(bi('् हलन्त: अक्षर का अ हटा देता है। पठ् = paṭh।', '् halant (virāma) removes the inherent अ. पठ् = paṭh.'), [line('प|saffron:ठ्', 'xl')], 'पठ्'),
                    beat(bi('संयुक्ताक्षर: बीच में स्वर न हो तो व्यञ्जन जुड़ जाते हैं: स् + त = स्त, क् + ष = क्ष।', 'Conjuncts: with no vowel between them, consonants join: स् + त = स्त, क् + ष = क्ष.'), [line('पु|saffron:स्त|कम्', 'xl')], 'पुस्तकम्')
                ]
            },
            word('जलम्', 'पानी', 'water', { icon: 'droplet' }),
            word('रामः', 'राम (नाम)', 'Rāma (a name)', { icon: 'user' }),
            mcq(bi('किस शब्द के अन्त में विसर्ग है?', 'Which word ends in a visarga?'), ['रामः', 'जलम्', 'फलम्'], 0, bi('रामः के अन्त में ः (विसर्ग) है।', 'रामः ends with ः, the visarga.'), 'devanagari'),
            mcq(bi('हलन्त (्) क्या करता है?', 'What does the halant (्) do?'), [bi('अक्षर का अ हटा देता है', 'removes the inherent अ'), bi('स्वर को दीर्घ करता है', 'makes the vowel long'), bi('ह जोड़ता है', 'adds an h')], 0, bi('क = ka, पर क् = k।', 'क = ka, but क् = k.'), 'devanagari'),
            { kind: 'arrange', prompt: bi('अक्षरों से "पुस्तकम्" (पुस्तक) बनाइए', 'Build the word पुस्तकम् (book) from its syllables'), pieces: ['पु', 'स्त', 'क', 'म्'], answer: 'पुस्तकम्', skill: 'reading' },
            { kind: 'arrange', prompt: bi('अक्षरों से "फलम्" (फल) बनाइए', 'Build the word फलम् (fruit)'), pieces: ['फ', 'ल', 'म्'], answer: 'फलम्', skill: 'reading' },
            listen('फलम्', ['फलम्', 'जलम्', 'कमलम्'], 'listening'),
            mcq(bi('क् + ष = ?', 'क् + ष = ?'), ['क्ष', 'क्स', 'ख'], 0, bi('क् और ष मिलकर विशेष आकार क्ष बनाते हैं।', 'क् and ष join into the special shape क्ष.'), 'devanagari'),
            { kind: 'trace', title: bi('संयुक्ताक्षर लिखिए', 'Trace the conjuncts'), letters: ['क्ष', 'त्र', 'ज्ञ'] }
        ]
    },
    {
        id: 'l06-greetings',
        number: 6,
        level: 1,
        moduleId: 'm1-words',
        title: bi('नमस्ते!', 'Greetings'),
        sa: 'अभिवादनम्',
        summary: bi('रोज़ काम आने वाले विनम्र शब्द।', 'Polite words you will use every day.'),
        minutes: 5,
        skills: ['vocabulary', 'listening', 'speaking'],
        vocab: ['namaste', 'suprabhatam', 'dhanyavadah', 'svagatam', 'ksamyatam', 'subharatrih', 'punah-milamah'],
        steps: [
            { kind: 'intro', art: 'guru', title: bi('बातचीत के पहले शब्द', 'Your first conversation words'), body: bi('संस्कृत में अभिवादन बहुत सुन्दर और सरल हैं। हर शब्द को सुनिए और दोहराइए।', 'Sanskrit greetings are short and lovely. Listen to each one and say it aloud.') },
            word('नमस्ते', 'नमस्ते', 'hello (lit. "salutation to you")', { icon: 'hand-heart', note: bi('नमः (नमस्कार) + ते (तुम्हें)।', 'नमः (salutation) + ते (to you).') }),
            word('सुप्रभातम्', 'सुप्रभात', 'good morning', { icon: 'sunrise' }),
            word('धन्यवादः', 'धन्यवाद', 'thank you', { icon: 'heart-handshake' }),
            word('स्वागतम्', 'स्वागत', 'welcome', { icon: 'sparkles' }),
            word('क्षम्यताम्', 'क्षमा करें', 'sorry, excuse me', { icon: 'heart' }),
            word('शुभरात्रिः', 'शुभ रात्रि', 'good night', { icon: 'moon' }),
            word('पुनः मिलामः', 'फिर मिलेंगे', 'see you again', { icon: 'hand' }),
            {
                kind: 'match',
                title: bi('शब्द और अर्थ मिलाइए', 'Match the greeting to its meaning'),
                pairs: [
                    { left: 'नमस्ते', right: bi('नमस्ते', 'hello') },
                    { left: 'सुप्रभातम्', right: bi('सुप्रभात', 'good morning') },
                    { left: 'धन्यवादः', right: bi('धन्यवाद', 'thank you') },
                    { left: 'शुभरात्रिः', right: bi('शुभ रात्रि', 'good night') },
                    { left: 'स्वागतम्', right: bi('स्वागत', 'welcome') }
                ],
                skill: 'vocabulary'
            },
            mcq(bi('सुबह गुरुजी से मिलने पर आप कहेंगे:', 'You meet your teacher in the morning. You say:'), ['सुप्रभातम्', 'शुभरात्रिः', 'क्षम्यताम्'], 0, bi('सुप्रभातम् = सुप्रभात।', 'सुप्रभातम् means good morning.'), 'vocabulary'),
            mcq(bi('कोई आपको पुस्तक देता है। आप कहेंगे:', 'Someone hands you a book. You say:'), ['धन्यवादः', 'स्वागतम्', 'पुनः मिलामः'], 0, bi('धन्यवादः = धन्यवाद।', 'धन्यवादः means thank you.'), 'vocabulary'),
            listen('धन्यवादः', ['धन्यवादः', 'स्वागतम्', 'नमस्ते']),
            { kind: 'speak', sa: 'नमस्ते', meaning: bi('नमस्ते', 'hello') }
        ]
    },
    {
        id: 'l07-objects',
        number: 7,
        level: 1,
        moduleId: 'm1-words',
        title: bi('आस-पास की वस्तुएँ', 'Things around you'),
        sa: 'वस्तूनि',
        summary: bi('पुस्तक, जल, फल, घर... और शब्दों के अन्त।', 'Book, water, fruit, house… and how words end.'),
        minutes: 6,
        skills: ['vocabulary', 'listening'],
        vocab: ['pustakam', 'jalam', 'phalam', 'grham', 'vrksah', 'lekhani', 'dipah'],
        steps: [
            { kind: 'intro', title: bi('रोज़ दिखने वाली चीज़ें', 'Everyday things'), body: bi('इन शब्दों के अन्त पर ध्यान दीजिए: -ः, -ई, -म्। अन्त हमें बहुत कुछ बताता है।', 'Watch how these words end: -ः, -ई, -म्. The ending tells us a lot.') },
            word('पुस्तकम्', 'पुस्तक', 'book', { icon: 'book' }),
            word('जलम्', 'पानी', 'water', { icon: 'droplet' }),
            word('फलम्', 'फल', 'fruit', { icon: 'apple' }),
            word('गृहम्', 'घर', 'house', { icon: 'house' }),
            word('वृक्षः', 'पेड़', 'tree', { icon: 'tree-deciduous' }),
            word('लेखनी', 'कलम', 'pen', { icon: 'pen-line' }),
            word('दीपः', 'दीपक', 'lamp', { icon: 'lamp' }),
            {
                kind: 'teach',
                title: bi('अन्त देखकर लिङ्ग', 'Endings hint at gender'),
                beats: [
                    beat(bi('-ः वाले शब्द प्रायः पुंलिङ्ग होते हैं: वृक्षः, दीपः।', 'Words in -ः are often masculine: वृक्षः, दीपः.'), [line('वृ|saffron:क्षः', 'lg'), line('दी|saffron:पः', 'lg')]),
                    beat(bi('-म् वाले प्रायः नपुंसकलिङ्ग: पुस्तकम्, जलम्, फलम्, गृहम्।', 'Words in -म् are often neuter: पुस्तकम्, जलम्, फलम्, गृहम्.'), [line('पुस्त|terracotta:कम्', 'lg'), line('ज|terracotta:लम्', 'lg')]),
                    beat(bi('-आ या -ई वाले प्रायः स्त्रीलिङ्ग: लेखनी, बालिका। लिङ्ग के बारे में आगे और सीखेंगे।', 'Words in -आ or -ई are often feminine: लेखनी, बालिका. We will learn more about gender later.'), [line('लेख|green:नी', 'lg')])
                ]
            },
            {
                kind: 'match',
                title: bi('शब्द और अर्थ', 'Words and meanings'),
                pairs: [
                    { left: 'पुस्तकम्', right: bi('पुस्तक', 'book') },
                    { left: 'जलम्', right: bi('पानी', 'water') },
                    { left: 'गृहम्', right: bi('घर', 'house') },
                    { left: 'वृक्षः', right: bi('पेड़', 'tree') },
                    { left: 'लेखनी', right: bi('कलम', 'pen') }
                ],
                skill: 'vocabulary'
            },
            mcq(bi('"दीपक" के लिए संस्कृत शब्द?', 'Which word means "lamp"?'), ['दीपः', 'घटः', 'फलम्'], 0, bi('दीपः = दीपक। घटः = घड़ा।', 'दीपः is a lamp; घटः is a pot.'), 'vocabulary'),
            listen('गृहम्', ['गृहम्', 'जलम्', 'फलम्']),
            mcq(bi('कौन सा शब्द -म् वाले (नपुंसक) शब्दों जैसा है?', 'Which word ends like a neuter (-म्) noun?'), ['जलम्', 'वृक्षः', 'लेखनी'], 0, bi('जलम् के अन्त में -म् है।', 'जलम् ends in -म्.'), 'vocabulary')
        ]
    },
    {
        id: 'l08-aham-tvam',
        number: 8,
        level: 1,
        moduleId: 'm1-pronouns',
        title: bi('मैं और तुम', 'I and you'),
        sa: 'अहम् त्वम् च',
        summary: bi('अहम् और त्वम् से पहले छोटे वाक्य।', 'First little sentences with अहम् and त्वम्.'),
        minutes: 5,
        skills: ['vocabulary', 'sentence'],
        vocab: ['aham', 'tvam', 'chatrah', 'balika'],
        steps: [
            { kind: 'intro', art: 'guru', title: bi('बातचीत के दो सबसे ज़रूरी शब्द', 'The two most important words in any conversation'), body: bi('मैं और तुम। इनसे हम अपने बारे में बता सकते हैं।', 'I and you. With them we can already talk about ourselves.') },
            word('अहम्', 'मैं', 'I', { figure: 'self' }),
            word('त्वम्', 'तुम', 'you (one person)', { figure: 'you' }),
            {
                kind: 'teach',
                title: bi('बिना "है" के वाक्य', 'Sentences without "am"'),
                beats: [
                    beat(bi('अहं बालकः। = मैं बालक हूँ। संस्कृत में "हूँ" शब्द अक्सर छोड़ दिया जाता है।', 'अहं बालकः = I am a boy. Sanskrit often leaves out "am".'), [line('अहं बालकः।', 'xl')], 'अहं बालकः।'),
                    beat(bi('त्वं बालिका। = तुम बालिका हो।', 'त्वं बालिका = You are a girl.'), [line('त्वं बालिका।', 'xl')], 'त्वं बालिका।'),
                    beat(bi('चाहें तो "अस्मि" (हूँ) भी जोड़ सकते हैं: अहं छात्रः अस्मि।', 'You may add अस्मि (am): अहं छात्रः अस्मि.'), [line('अहं छात्रः |saffron:अस्मि।', 'lg')], 'अहं छात्रः अस्मि।'),
                    beat(bi('व्यञ्जन से पहले अहम् का म् अनुस्वार बन जाता है: अहं बालकः। शब्द वही है।', 'Before a consonant, the म् of अहम् is written as anusvāra: अहं बालकः. It is the same word.'), [line('अह|saffron:म्|  →  अ|saffron:हं', 'lg')])
                ]
            },
            mcq(bi('संस्कृत में "मैं"?', '"I" in Sanskrit is…'), ['अहम्', 'त्वम्', 'सः'], 0, bi('अहम् = मैं।', 'अहम् means I.'), 'vocabulary'),
            mcq(bi('"त्वं बालिका।" का अर्थ?', 'What does त्वं बालिका mean?'), [bi('तुम बालिका हो।', 'You are a girl.'), bi('मैं बालिका हूँ।', 'I am a girl.'), bi('वह बालिका है।', 'She is a girl.')], 0, bi('त्वम् = तुम।', 'त्वम् means you.'), 'reading'),
            build(bi('बनाइए: "मैं छात्र हूँ।"', 'Build: "I am a student."'), ['अहं', 'छात्रः', 'त्वं', 'बालिका'], ['अहं', 'छात्रः']),
            build(bi('बनाइए: "तुम बालक हो।"', 'Build: "You are a boy."'), ['त्वं', 'बालकः', 'अहं', 'छात्रा'], ['त्वं', 'बालकः']),
            listen('अहम्', ['अहम्', 'त्वम्', 'सः']),
            { kind: 'speak', sa: 'अहं छात्रः अस्मि।', meaning: bi('मैं छात्र हूँ।', 'I am a student.') }
        ]
    },
    {
        id: 'l09-sah-sa-tat',
        number: 9,
        level: 1,
        moduleId: 'm1-pronouns',
        title: bi('वह (पुरुष, स्त्री, वस्तु)', 'He, she, it'),
        sa: 'सः सा तत्',
        summary: bi('शब्द के लिङ्ग के अनुसार सर्वनाम चुनना।', 'Choosing the pronoun that matches a word’s gender.'),
        minutes: 6,
        skills: ['vocabulary', 'sentence'],
        vocab: ['sah', 'sa', 'tat', 'esah', 'esa', 'etat'],
        steps: [
            { kind: 'intro', title: bi('किसी के बारे में बात करना', 'Talking about someone'), body: bi('जब हम किसी और के बारे में बोलते हैं, तो सर्वनाम उस शब्द के लिङ्ग के अनुसार बदलता है।', 'When we talk about someone or something else, the pronoun follows the word’s gender.') },
            word('सः', 'वह (पुरुष)', 'he, that (masc.)', { figure: 'other', note: bi('पुंलिङ्ग शब्दों के लिए: सः बालकः।', 'For masculine words: सः बालकः.') }),
            word('सा', 'वह (स्त्री)', 'she, that (fem.)', { figure: 'other', note: bi('स्त्रीलिङ्ग शब्दों के लिए: सा बालिका।', 'For feminine words: सा बालिका.') }),
            word('तत्', 'वह (वस्तु)', 'it, that (neut.)', { icon: 'book', note: bi('नपुंसकलिङ्ग शब्दों के लिए: तत् पुस्तकम्।', 'For neuter words: तत् पुस्तकम्.') }),
            {
                kind: 'teach',
                title: bi('लिङ्ग के अनुसार', 'By gender'),
                beats: [
                    beat(bi('सः → बालकः, वृक्षः, दीपः (पुंलिङ्ग)', 'सः → बालकः, वृक्षः, दीपः (masculine)'), [line('saffron:सः  →  बालकः', 'lg')]),
                    beat(bi('सा → बालिका, लेखनी, माता (स्त्रीलिङ्ग)', 'सा → बालिका, लेखनी, माता (feminine)'), [line('terracotta:सा  →  बालिका', 'lg')]),
                    beat(bi('तत् → पुस्तकम्, जलम्, फलम् (नपुंसकलिङ्ग)', 'तत् → पुस्तकम्, जलम्, फलम् (neuter)'), [line('green:तत्  →  पुस्तकम्', 'lg')]),
                    beat(bi('पास की चीज़ के लिए: एषः (यह), एषा, एतत्। दूर के लिए: सः, सा, तत्।', 'For something near: एषः, एषा, एतत् (this). For something far: सः, सा, तत् (that).'), [line('एषः  एषा  एतत्', 'lg', bi('यह (पास)', 'this (near)')), line('सः  सा  तत्', 'lg', bi('वह (दूर)', 'that (far)'))])
                ]
            },
            mcq(bi('वृक्षः के लिए कौन सा सर्वनाम?', 'Which pronoun goes with वृक्षः (tree)?'), ['सः', 'सा', 'तत्'], 0, bi('वृक्षः पुंलिङ्ग है, इसलिए सः।', 'वृक्षः is masculine, so सः.'), 'vocabulary'),
            mcq(bi('पुस्तकम् के लिए?', 'Which pronoun goes with पुस्तकम्?'), ['तत्', 'सः', 'सा'], 0, bi('पुस्तकम् नपुंसकलिङ्ग है, इसलिए तत्।', 'पुस्तकम् is neuter, so तत्.'), 'vocabulary'),
            mcq(bi('लेखनी के लिए?', 'Which pronoun goes with लेखनी (pen)?'), ['सा', 'सः', 'तत्'], 0, bi('लेखनी स्त्रीलिङ्ग है, इसलिए सा।', 'लेखनी is feminine, so सा.'), 'vocabulary'),
            {
                kind: 'match',
                title: bi('सर्वनाम और अर्थ', 'Pronouns and meanings'),
                pairs: [
                    { left: 'सः', right: bi('वह (पुरुष)', 'he') },
                    { left: 'सा', right: bi('वह (स्त्री)', 'she') },
                    { left: 'तत्', right: bi('वह (वस्तु)', 'it, that') },
                    { left: 'एषः', right: bi('यह (पुरुष)', 'this (masc.)') }
                ],
                skill: 'vocabulary'
            },
            build(bi('बनाइए: "वह बालिका है।"', 'Build: "She is a girl."'), ['सा', 'बालिका', 'सः', 'बालकः'], ['सा', 'बालिका']),
            build(bi('बनाइए: "यह पुस्तक है।"', 'Build: "This is a book."'), ['एतत्', 'पुस्तकम्', 'एषः', 'सा'], ['एतत्', 'पुस्तकम्'])
        ]
    },
    {
        id: 'l10-ekavacana',
        number: 10,
        level: 2,
        moduleId: 'm2-vacana',
        title: bi('एक', 'One'),
        sa: 'एकवचनम्',
        summary: bi('संस्कृत तीन प्रकार से गिनती है। पहला: एक।', 'Sanskrit counts in three ways. First: exactly one.'),
        minutes: 4,
        skills: ['declension', 'vocabulary'],
        steps: [
            { kind: 'intro', art: 'guru', title: bi('एक, दो, अनेक', 'One, two, many'), body: bi('संस्कृत संख्या को तीन तरह से दिखाती है: एक, दो, और तीन या अधिक। पहले "एक" देखते हैं।', 'Sanskrit shows number in three ways: one, two, and three-or-more. We start with one.') },
            { kind: 'vacana', title: bi('एक बालक', 'One boy'), forms: { eka: 'बालकः', dvi: 'बालकौ', bahu: 'बालकाः' }, meaning: bi('बालक', 'boy'), figure: 'boy', reveal: ['eka'] },
            {
                kind: 'teach',
                title: bi('एकवचन', 'The singular'),
                beats: [
                    beat(bi('एक बालक: बालकः', 'One boy: बालकः'), [line('बालकः', 'xl', bi('एक बालक', 'one boy'))], 'बालकः'),
                    beat(bi('एक बालिका: बालिका', 'One girl: बालिका'), [line('बालिका', 'xl', bi('एक बालिका', 'one girl'))], 'बालिका'),
                    beat(bi('एक फल: फलम्। इन्हें एकवचन कहते हैं।', 'One fruit: फलम्. These are एकवचन (singular) forms.'), [line('फलम्', 'xl', bi('एक फल', 'one fruit'))], 'फलम्')
                ]
            },
            mcq(bi('"बालकः" कितने बालक हैं?', 'How many boys does बालकः mean?'), [bi('एक', 'one'), bi('दो', 'two'), bi('तीन या अधिक', 'three or more')], 0, bi('बालकः = एक बालक (एकवचन)।', 'बालकः = one boy (singular).'), 'declension'),
            mcq(bi('इनमें एकवचन कौन है?', 'Which form is singular?'), ['बालिका', 'बालिके', 'बालिकाः'], 0, bi('बालिका एकवचन है।', 'बालिका is singular.'), 'declension'),
            listen('बालकः', ['बालकः', 'बालकौ', 'बालकाः'])
        ]
    },
    {
        id: 'l11-dvivacana',
        number: 11,
        level: 2,
        moduleId: 'm2-vacana',
        title: bi('दो', 'Two'),
        sa: 'द्विवचनम्',
        summary: bi('ठीक दो के लिए विशेष रूप: द्विवचन।', 'A special form for exactly two: the dual.'),
        minutes: 6,
        skills: ['declension', 'conjugation'],
        steps: [
            { kind: 'intro', title: bi('ठीक दो के लिए अलग रूप', 'A form just for two'), body: bi('अंग्रेज़ी और हिन्दी में केवल एकवचन और बहुवचन हैं। संस्कृत में ठीक दो के लिए अलग रूप है: द्विवचन। पहले देखिए, फिर नियम समझिए।', 'English and Hindi have only singular and plural. Sanskrit has a separate form for exactly two: the dual (द्विवचन). Look first, then find the pattern.') },
            { kind: 'vacana', title: bi('बालक', 'Boy'), forms: { eka: 'बालकः', dvi: 'बालकौ', bahu: 'बालकाः' }, meaning: bi('बालक', 'boy'), figure: 'boy', reveal: ['eka', 'dvi'] },
            { kind: 'vacana', title: bi('बालिका', 'Girl'), forms: { eka: 'बालिका', dvi: 'बालिके', bahu: 'बालिकाः' }, meaning: bi('बालिका', 'girl'), figure: 'girl', reveal: ['eka', 'dvi'] },
            { kind: 'vacana', title: bi('फल', 'Fruit'), forms: { eka: 'फलम्', dvi: 'फले', bahu: 'फलानि' }, meaning: bi('फल', 'fruit'), figure: 'fruit', reveal: ['eka', 'dvi'] },
            {
                kind: 'teach',
                title: bi('नियम पहचानिए', 'Spot the pattern'),
                beats: [
                    beat(bi('बालकः → बालकौ: पुंलिङ्ग में -औ।', 'बालकः → बालकौ: masculine words take -औ.'), [line('बाल|saffron:कौ', 'xl')], 'बालकौ'),
                    beat(bi('बालिका → बालिके: आकारान्त स्त्रीलिङ्ग में -ए।', 'बालिका → बालिके: feminine words in -आ take -ए.'), [line('बालि|terracotta:के', 'xl')], 'बालिके'),
                    beat(bi('फलम् → फले: नपुंसकलिङ्ग में भी -ए।', 'फलम् → फले: neuter words also take -ए.'), [line('green:फले', 'xl')], 'फले'),
                    beat(bi('क्रिया का भी द्विवचन होता है: बालकौ पठतः = दो बालक पढ़ते हैं।', 'Verbs have a dual too: बालकौ पठतः = two boys read.'), [line('बालकौ |saffron:पठतः।', 'xl')], 'बालकौ पठतः।')
                ]
            },
            mcq(bi('दो बालक = ?', 'Two boys = ?'), ['बालकौ', 'बालकाः', 'बालकः'], 0, bi('पुंलिङ्ग द्विवचन में -औ: बालकौ।', 'Masculine dual takes -औ: बालकौ.'), 'declension', { item: 'concept:dvivacana' }),
            mcq(bi('दो फल = ?', 'Two fruits = ?'), ['फले', 'फलानि', 'फलम्'], 0, bi('नपुंसक द्विवचन: फले।', 'Neuter dual: फले.'), 'declension', { item: 'concept:dvivacana' }),
            fill(bi('दो बालिकाएँ:', 'Two girls:'), '', '', ['बालिके', 'बालिकाः', 'बालिका'], 'बालिके', bi('आकारान्त स्त्रीलिङ्ग द्विवचन: बालिके। (हे बालिके! = "हे बालिका" भी इसी रूप से कहा जाता है।)', 'Feminine dual: बालिके. (The same form is used when calling one girl: हे बालिके!)'), 'declension', 'concept:dvivacana'),
            {
                kind: 'match',
                title: bi('एक से दो बनाइए', 'Turn one into two'),
                pairs: [
                    { left: 'बालकः', right: 'बालकौ' },
                    { left: 'बालिका', right: 'बालिके' },
                    { left: 'फलम्', right: 'फले' },
                    { left: 'वृक्षः', right: 'वृक्षौ' }
                ],
                skill: 'declension'
            },
            mcq(bi('"बालकौ पठतः" का अर्थ?', 'What does बालकौ पठतः mean?'), [bi('दो बालक पढ़ते हैं', 'Two boys read'), bi('कई बालक पढ़ते हैं', 'Many boys read'), bi('एक बालक पढ़ता है', 'One boy reads')], 0, bi('बालकौ (दो) और पठतः (दो के लिए क्रिया)।', 'बालकौ (two boys) and पठतः (verb for two).'), 'conjugation')
        ]
    },
    {
        id: 'l12-bahuvacana',
        number: 12,
        level: 2,
        moduleId: 'm2-vacana',
        title: bi('अनेक', 'Many'),
        sa: 'बहुवचनम्',
        summary: bi('तीन या अधिक के लिए बहुवचन।', 'Three or more: the plural.'),
        minutes: 5,
        skills: ['declension', 'listening'],
        steps: [
            { kind: 'intro', title: bi('तीन या अधिक', 'Three or more'), body: bi('तीन या उससे अधिक के लिए बहुवचन का प्रयोग होता है।', 'For three or more we use the plural (बहुवचन).') },
            { kind: 'vacana', title: bi('बालक', 'Boy'), forms: { eka: 'बालकः', dvi: 'बालकौ', bahu: 'बालकाः' }, meaning: bi('बालक', 'boy'), figure: 'boy' },
            { kind: 'vacana', title: bi('बालिका', 'Girl'), forms: { eka: 'बालिका', dvi: 'बालिके', bahu: 'बालिकाः' }, meaning: bi('बालिका', 'girl'), figure: 'girl' },
            { kind: 'vacana', title: bi('फल', 'Fruit'), forms: { eka: 'फलम्', dvi: 'फले', bahu: 'फलानि' }, meaning: bi('फल', 'fruit'), figure: 'fruit' },
            {
                kind: 'teach',
                title: bi('बहुवचन के अन्त', 'Plural endings'),
                beats: [
                    beat(bi('बालकाः (-आः), बालिकाः (-आः), फलानि (-आनि)।', 'बालकाः (-आः), बालिकाः (-आः), फलानि (-आनि).'), [line('बाल|saffron:काः', 'lg'), line('बालि|saffron:काः', 'lg'), line('फ|saffron:लानि', 'lg')]),
                    beat(bi('सावधान! बालकः (एक) और बालकाः (अनेक) में केवल लम्बे आ का अन्तर है।', 'Careful! बालकः (one) and बालकाः (many) differ only by the long ā.'), [line('बालकः  ≠  बाल|terracotta:काः', 'lg')], 'बालकः बालकाः')
                ]
            },
            mcq(bi('अनेक फल = ?', 'Many fruits = ?'), ['फलानि', 'फले', 'फलम्'], 0, bi('नपुंसक बहुवचन: फलानि।', 'Neuter plural: फलानि.'), 'declension', { item: 'concept:bahuvacana' }),
            mcq(bi('"बालकाः" का अर्थ?', 'What does बालकाः mean?'), [bi('कई बालक', 'many boys'), bi('दो बालक', 'two boys'), bi('एक बालक', 'one boy')], 0, bi('-आः बहुवचन है।', '-आः marks the plural.'), 'declension', { item: 'concept:bahuvacana' }),
            listen('बालिकाः', ['बालिका', 'बालिके', 'बालिकाः']),
            {
                kind: 'match',
                title: bi('रूप और संख्या', 'Form and number'),
                pairs: [
                    { left: 'बालकाः', right: bi('कई बालक', 'many boys') },
                    { left: 'बालकौ', right: bi('दो बालक', 'two boys') },
                    { left: 'बालकः', right: bi('एक बालक', 'one boy') }
                ],
                skill: 'declension'
            },
            mcq(bi('तीन पेड़ों के लिए सही रूप?', 'The right form for three trees?'), ['वृक्षाः', 'वृक्षौ', 'वृक्षः'], 0, bi('तीन या अधिक = बहुवचन: वृक्षाः।', 'Three or more = plural: वृक्षाः.'), 'declension', { item: 'concept:bahuvacana' })
        ]
    },
    {
        id: 'l13-purusha',
        number: 13,
        level: 2,
        moduleId: 'm2-purusha',
        title: bi('कौन बोल रहा है?', 'Who is speaking?'),
        sa: 'पुरुषः',
        summary: bi('उत्तम, मध्यम, प्रथम पुरुष और 3 × 3 तालिका।', 'उत्तम, मध्यम and प्रथम पुरुष, and the 3 × 3 grid.'),
        minutes: 7,
        skills: ['conjugation', 'vocabulary'],
        steps: [
            { kind: 'intro', art: 'guru', title: bi('हर क्रिया बताती है: कौन?', 'Every verb tells you: who?'), body: bi('वक्ता, सुनने वाला, या कोई और: संस्कृत इन्हें पुरुष कहती है।', 'The speaker, the listener, or someone else: Sanskrit calls these पुरुष.') },
            {
                kind: 'teach',
                title: bi('तीन पुरुष', 'The three persons'),
                beats: [
                    beat(bi('उत्तम पुरुष = जो बोल रहा है: अहम् (मैं), आवाम् (हम दो), वयम् (हम सब)।', 'उत्तम पुरुष = the speaker: अहम् (I), आवाम् (we two), वयम् (we all).'), [line('saffron:अहम्  आवाम्  वयम्', 'lg', bi('उत्तम पुरुष', 'उत्तम पुरुष'))], 'अहम् आवाम् वयम्'),
                    beat(bi('मध्यम पुरुष = जिससे बात हो रही है: त्वम्, युवाम्, यूयम्।', 'मध्यम पुरुष = the one spoken to: त्वम्, युवाम्, यूयम्.'), [line('terracotta:त्वम्  युवाम्  यूयम्', 'lg', bi('मध्यम पुरुष', 'मध्यम पुरुष'))], 'त्वम् युवाम् यूयम्'),
                    beat(bi('प्रथम पुरुष = बाकी सब: सः, तौ, ते, और बालकः जैसे सभी संज्ञा-शब्द।', 'प्रथम पुरुष = everyone else: सः, तौ, ते, and every noun such as बालकः.'), [line('green:सः  तौ  ते', 'lg', bi('प्रथम पुरुष', 'प्रथम पुरुष'))], 'सः तौ ते'),
                    beat(bi('ध्यान दें! अंग्रेज़ी का "first person" (I) संस्कृत में उत्तम पुरुष है, और "third person" (he) प्रथम पुरुष। क्रम उल्टा है!', 'Careful! English "first person" (I) is Sanskrit उत्तम पुरुष, and English "third person" (he) is प्रथम पुरुष. The order is reversed!'), [line('I = उत्तम', 'md'), line('he = प्रथम', 'md')])
                ]
            },
            { kind: 'grid', title: bi('पुरुष-वचन तालिका', 'The पुरुष × वचन grid'), mode: 'pronoun' },
            mcq(bi('अहम् किस पुरुष में है?', 'Which पुरुष is अहम्?'), ['उत्तम', 'मध्यम', 'प्रथम'], 0, bi('वक्ता = उत्तम पुरुष।', 'The speaker = उत्तम पुरुष.'), 'conjugation', { item: 'concept:uttama' }),
            mcq(bi('बालकः (एक संज्ञा) किस पुरुष में है?', 'Which पुरुष is a noun like बालकः?'), ['प्रथम', 'मध्यम', 'उत्तम'], 0, bi('हर संज्ञा प्रथम पुरुष में होती है।', 'Every noun is प्रथम पुरुष.'), 'conjugation', { item: 'concept:prathama' }),
            mcq(bi('युवाम् का अर्थ?', 'What does युवाम् mean?'), [bi('तुम दोनों', 'you two'), bi('हम दोनों', 'we two'), bi('वे दोनों', 'they two')], 0, bi('युवाम् = मध्यम पुरुष द्विवचन।', 'युवाम् = मध्यम पुरुष, dual.'), 'vocabulary'),
            {
                kind: 'match',
                title: bi('सर्वनाम और अर्थ', 'Pronouns and meanings'),
                pairs: [
                    { left: 'अहम्', right: bi('मैं', 'I') },
                    { left: 'आवाम्', right: bi('हम दोनों', 'we two') },
                    { left: 'वयम्', right: bi('हम सब', 'we (all)') },
                    { left: 'त्वम्', right: bi('तुम', 'you (one)') },
                    { left: 'यूयम्', right: bi('तुम सब', 'you (all)') }
                ],
                skill: 'vocabulary'
            },
            mcq(bi('तौ का अर्थ?', 'What does तौ mean?'), [bi('वे दोनों (पुरुष)', 'they two (masc.)'), bi('हम दोनों', 'we two'), bi('तुम दोनों', 'you two')], 0, bi('तौ = प्रथम पुरुष द्विवचन।', 'तौ = प्रथम पुरुष, dual.'), 'vocabulary')
        ]
    },
    {
        id: 'l14-dhatu',
        number: 14,
        level: 3,
        moduleId: 'm3-dhatu',
        title: bi('धातु: क्रिया की जड़', 'The verb root'),
        sa: 'धातुः',
        summary: bi('पठ्, लिख्, गम्, खाद्, पा: मूल अर्थ देने वाले धातु।', 'पठ्, लिख्, गम्, खाद्, पा: roots that carry the basic meaning.'),
        minutes: 5,
        skills: ['conjugation', 'vocabulary'],
        steps: [
            { kind: 'intro', title: bi('शब्दों की जड़', 'The roots of words'), body: bi('संस्कृत क्रियाएँ धातुओं से बनती हैं। धातु मूल अर्थ देता है, और प्रत्यय बताता है कौन और कब।', 'Sanskrit verbs grow from roots (धातु). The root gives the basic meaning; the ending tells who and when.') },
            {
                kind: 'teach',
                title: bi('एक धातु, अनेक शब्द', 'One root, many words'),
                beats: [
                    beat(bi('पठ् = पढ़ना। ् (हलन्त) बताता है कि यह अभी अधूरा धातु है, पूरा शब्द नहीं।', 'पठ् = to read. The ् shows it is a bare root, not yet a word.'), [line('saffron:पठ्', 'xl', bi('पढ़ना', 'to read'))], 'पठ्'),
                    beat(bi('धातु + प्रत्यय = क्रिया: पठ् से पठति (पढ़ता है)। इसके चरण हम अगले पाठ में देखेंगे।', 'Root + ending = verb: from पठ् comes पठति (reads). We will see the exact steps in the next lesson.'), [line('पठ्  →  पठ|saffron:ति', 'lg')], 'पठति'),
                    beat(bi('एक धातु से अनेक शब्द: पाठः (पाठ), पाठकः (पढ़ने वाला), पठनम् (पढ़ना)।', 'One root, many words: पाठः (lesson), पाठकः (reader), पठनम् (reading).'), [line('पाठः  पाठकः  पठनम्', 'lg')])
                ]
            },
            word('पठ्', 'पढ़ना', 'to read', { icon: 'book-open' }),
            word('लिख्', 'लिखना', 'to write', { icon: 'pen-line' }),
            word('गम्', 'जाना', 'to go', { icon: 'footprints', note: bi('वर्तमान में इसका रूप गच्छ बनता है: गच्छति।', 'In the present it uses the stem गच्छ: गच्छति.') }),
            word('खाद्', 'खाना', 'to eat', { icon: 'utensils' }),
            word('पा', 'पीना', 'to drink', { icon: 'glass-water', note: bi('वर्तमान में इसका अङ्ग पिब बनता है: पिबति।', 'In the present it uses the stem पिब: पिबति.') }),
            {
                kind: 'match',
                title: bi('धातु और अर्थ', 'Roots and meanings'),
                pairs: [
                    { left: 'पठ्', right: bi('पढ़ना', 'to read') },
                    { left: 'लिख्', right: bi('लिखना', 'to write') },
                    { left: 'गम्', right: bi('जाना', 'to go') },
                    { left: 'खाद्', right: bi('खाना', 'to eat') },
                    { left: 'पा', right: bi('पीना', 'to drink') }
                ],
                skill: 'vocabulary'
            },
            mcq(bi('पठति का धातु?', 'What is the root of पठति?'), ['पठ्', 'पति', 'पठा'], 0, bi('पठति = पठ् + अ + ति।', 'पठति = पठ् + अ + ति.'), 'conjugation'),
            mcq(bi('गच्छति (जाता है) किस धातु से बना है?', 'गच्छति (goes) comes from which root?'), ['गम्', 'गच्छ्', 'गा'], 0, bi('गम् का वर्तमान अङ्ग गच्छ है (इषुगमियमां छः)।', 'गम् has the special present stem गच्छ (इषुगमियमां छः).'), 'conjugation')
        ]
    },
    {
        id: 'l15-first-sentence',
        number: 15,
        level: 3,
        moduleId: 'm3-dhatu',
        taster: true,
        title: bi('मेरा पहला संस्कृत वाक्य', 'My first Sanskrit sentence'),
        sa: 'मम प्रथमं संस्कृतवाक्यम्',
        summary: bi('"अहं पठामि" बनाना सीखें, फिर त्वं पठसि और सः पठति।', 'Build "अहं पठामि" (I read), then त्वं पठसि and सः पठति.'),
        minutes: 7,
        skills: ['conjugation', 'sentence', 'speaking', 'verb-production'],
        vocab: ['aham', 'pathati', 'tvam', 'sah'],
        complete: { sa: 'अभिनन्दनम्!', text: bi('आपने संस्कृत में अपने पहले वाक्य बनाए।', 'You have made your first sentences in Sanskrit.') },
        steps: [
            {
                kind: 'intro',
                art: 'guru',
                title: bi('आज का लक्ष्य: "मैं पढ़ता हूँ"', 'Today’s goal: "I read"'),
                body: bi('आज हम संस्कृत में अपना पहला वाक्य बनाना सीखेंगे।', 'Today we will learn to make our first sentence in Sanskrit.'),
                sa: 'अहं पठामि।'
            },
            word('अहम्', 'मैं', 'I', { figure: 'self', note: bi('जो स्वयं बोल रहा है।', 'The person who is speaking.') }),
            word('पठ्', 'पढ़ना', 'to read', { icon: 'book-open', note: bi('धातु (क्रिया की जड़)।', 'A verb root.') }),
            {
                kind: 'teach',
                title: bi('वर्तमान काल: लट् लकार', 'The present: लट् lakāra'),
                beats: [
                    beat(bi('लट् लकार वर्तमान काल में उपयोग किया जाता है।', 'The लट् lakāra is used for the present tense.'), [line('saffron:लट्  =  वर्तमान काल', 'lg', bi('अभी हो रहा है', 'happening now'))]),
                    beat(bi('पठ् का लट् रूप बताएगा: "पढ़ता है / पढ़ रहा है"।', 'पठ् in लट् means "reads" or "is reading".'), [line('पठ्  +  लट्', 'lg')])
                ]
            },
            {
                kind: 'teach',
                title: bi('पुरुष और वचन', 'Person and number'),
                beats: [
                    beat(bi('अहम् = वक्ता स्वयं: उत्तम पुरुष।', 'अहम् is the speaker: उत्तम पुरुष.'), [line('saffron:उत्तम पुरुष', 'lg')]),
                    beat(bi('और केवल एक व्यक्ति: एकवचन।', 'And just one person: एकवचन.'), [line('saffron:उत्तम पुरुष|  +  |terracotta:एकवचन', 'lg')]),
                    beat(bi('तो हमें चाहिए: पठ्, लट्, उत्तम पुरुष, एकवचन।', 'So we need: पठ्, लट्, उत्तम पुरुष, एकवचन.'), [line('पठ् · लट् · उत्तम · एक', 'md')])
                ]
            },
            {
                kind: 'morph',
                title: bi('क्रिया बनाइए', 'Build the verb'),
                formation: { dhatuId: 'path', lakara: 'lat', pada: 'P', purusha: 'uttama', vacana: 'eka' },
                note: bi('पठमि गलत है! म से पहले अङ्ग का अ दीर्घ आ हो जाता है: पठामि।', 'पठमि is wrong! Before म the stem’s अ becomes long आ: पठामि.')
            },
            build(bi('टाइल जोड़कर वाक्य बनाइए: "मैं पढ़ता हूँ।"', 'Join the tiles: "I read."'), ['अहं', 'पठामि'], ['अहं', 'पठामि'], 'sentence', { item: 'verb:path:lat:P:uttama:eka' }),
            { kind: 'speak', sa: 'अहं पठामि।', meaning: bi('मैं पढ़ता/पढ़ती हूँ।', 'I read.') },
            mcq(bi('"I read" है:', '"I read" is:'), ['अहं पठामि।', 'त्वं पठसि।', 'सः पठति।'], 0, bi('अहम् (मैं) के साथ उत्तम पुरुष एकवचन: पठामि।', 'With अहम् (I) we need उत्तम पुरुष singular: पठामि.'), 'conjugation', { item: 'verb:path:lat:P:uttama:eka' }),
            fill(bi('रिक्त स्थान भरिए', 'Fill in the blank'), 'अहं ', '।', ['पठति', 'पठसि', 'पठामि'], 'पठामि', bi('अहम् के साथ सदा -आमि: पठामि।', 'With अहम् the ending is -आमि: पठामि.'), 'verb-production', 'verb:path:lat:P:uttama:eka'),
            {
                kind: 'teach',
                title: bi('तुम पढ़ते हो', 'You read'),
                beats: [
                    beat(bi('त्वम् = वह जिससे बात हो रही है: मध्यम पुरुष, एकवचन।', 'त्वम् is the one spoken to: मध्यम पुरुष, singular.'), [line('त्वं पठ|terracotta:सि।', 'xl')], 'त्वं पठसि।'),
                    beat(bi('पठ + सि = पठसि। यहाँ अ लम्बा नहीं होता, क्योंकि सि म या व से शुरू नहीं होता।', 'पठ + सि = पठसि. No lengthening here, because सि does not start with म or व.'), [line('पठ + सि = पठसि', 'lg')])
                ]
            },
            {
                kind: 'teach',
                title: bi('वह पढ़ता है', 'He reads'),
                beats: [
                    beat(bi('सः = कोई और: प्रथम पुरुष, एकवचन।', 'सः is someone else: प्रथम पुरुष, singular.'), [line('सः पठ|green:ति।', 'xl')], 'सः पठति।'),
                    beat(bi('पठ + ति = पठति।', 'पठ + ति = पठति.'), [line('पठ + ति = पठति', 'lg')])
                ]
            },
            {
                kind: 'match',
                title: bi('सर्वनाम और क्रिया जोड़िए', 'Match each pronoun with its verb'),
                pairs: [
                    { left: 'अहम्', right: 'पठामि' },
                    { left: 'त्वम्', right: 'पठसि' },
                    { left: 'सः', right: 'पठति' }
                ],
                skill: 'conjugation'
            },
            build(bi('बनाइए: "मैं पढ़ता हूँ।"', 'Build: "I read."'), ['अहं', 'पठामि', 'पठति', 'त्वं'], ['अहं', 'पठामि'], 'sentence', { item: 'verb:path:lat:P:uttama:eka' }),
            build(bi('बनाइए: "तुम पढ़ते हो।"', 'Build: "You read."'), ['त्वं', 'पठसि', 'पठामि', 'सः'], ['त्वं', 'पठसि'], 'sentence', { item: 'verb:path:lat:P:madhyama:eka' }),
            build(bi('बनाइए: "वह पढ़ता है।"', 'Build: "He reads."'), ['सः', 'पठति', 'पठसि', 'अहं'], ['सः', 'पठति'], 'sentence', { item: 'verb:path:lat:P:prathama:eka' })
        ]
    },
    {
        id: 'l16-simple-sentences',
        number: 16,
        level: 3,
        moduleId: 'm3-dhatu',
        title: bi('सरल वाक्य', 'Simple sentences'),
        sa: 'सरलवाक्यानि',
        summary: bi('कर्ता + क्रिया, और कर्ता + कर्म + क्रिया।', 'Subject + verb, and subject + object + verb.'),
        minutes: 6,
        skills: ['sentence', 'reading', 'listening'],
        steps: [
            { kind: 'intro', title: bi('हर संज्ञा प्रथम पुरुष है', 'Every noun is प्रथम पुरुष'), body: bi('इसलिए जब कर्ता एक संज्ञा हो (बालकः, बालिका), क्रिया सः वाली होती है: -ति।', 'So when the subject is a single noun (बालकः, बालिका), the verb is the same as with सः: -ति.') },
            {
                kind: 'teach',
                title: bi('वाक्य बनाना', 'Making sentences'),
                beats: [
                    beat(bi('बालकः पठति। बालक पढ़ता है।', 'बालकः पठति. The boy reads.'), [line('बालकः पठति।', 'xl')], 'बालकः पठति।'),
                    beat(bi('बालिका लिखति। बालिका लिखती है।', 'बालिका लिखति. The girl writes.'), [line('बालिका लिखति।', 'xl')], 'बालिका लिखति।'),
                    beat(bi('अब कर्म जोड़ें (क्या पढ़ता है?): बालकः पुस्तकं पठति।', 'Now add an object (reads what?): बालकः पुस्तकं पठति.'), [line('बालकः |saffron:पुस्तकं| पठति।', 'xl', bi('बालक पुस्तक पढ़ता है।', 'The boy reads a book.'))], 'बालकः पुस्तकं पठति।'),
                    beat(bi('व्यञ्जन से पहले पुस्तकम् का म् अनुस्वार बनता है: पुस्तकं। शब्द वही है।', 'Before a consonant, पुस्तकम् is written पुस्तकं. Same word.'), [line('पुस्त|saffron:कम्|  →  पुस्त|saffron:कं', 'lg')])
                ]
            },
            build(bi('बनाइए: "बालिका लिखती है।"', 'Build: "The girl writes."'), ['बालिका', 'लिखति', 'लिखामि', 'बालकः'], ['बालिका', 'लिखति']),
            build(bi('बनाइए: "बालक पुस्तक पढ़ता है।"', 'Build: "The boy reads a book."'), ['बालकः', 'पुस्तकं', 'पठति', 'पठसि'], ['बालकः', 'पुस्तकं', 'पठति'], 'sentence', { orderFree: true }),
            mcq(bi('"रामः जलं पिबति।" का अर्थ?', 'What does रामः जलं पिबति mean?'), [bi('राम पानी पीता है।', 'Rāma drinks water.'), bi('राम पानी देखता है।', 'Rāma sees water.'), bi('राम पानी लाता है।', 'Rāma brings water.')], 0, bi('पिबति = पीता है।', 'पिबति means drinks.'), 'reading'),
            build(bi('बनाइए: "बंदर फल खाता है।"', 'Build: "The monkey eats a fruit."'), ['वानरः', 'फलं', 'खादति', 'खादामि', 'पिबति'], ['वानरः', 'फलं', 'खादति'], 'sentence', { orderFree: true }),
            fill(bi('हाथी वन में चलता है।', 'The elephant walks in the forest.'), 'गजः वने ', '।', ['चलति', 'चलामि', 'चलसि'], 'चलति', bi('गजः (एक संज्ञा) = प्रथम पुरुष एकवचन: चलति।', 'गजः (a noun) = प्रथम पुरुष singular: चलति.'), 'verb-production'),
            listen('बालिका लिखति।', ['बालिका लिखति।', 'बालकः लिखति।', 'बालिका पठति।']),
            { kind: 'speak', sa: 'बालकः पुस्तकं पठति।', meaning: bi('बालक पुस्तक पढ़ता है।', 'The boy reads a book.') }
        ]
    },
    {
        id: 'l17-more-lat',
        number: 17,
        level: 3,
        moduleId: 'm3-dhatu',
        title: bi('लट् के और रूप', 'More present-tense forms'),
        sa: 'लट्लकारः',
        summary: bi('गम् की पूरी 3 × 3 तालिका, द्विवचन सहित।', 'The full 3 × 3 table of गम्, dual included.'),
        minutes: 7,
        skills: ['conjugation', 'verb-production', 'sentence'],
        steps: [
            { kind: 'intro', title: bi('पूरी तालिका', 'The whole table'), body: bi('अधिकतर धातु पठ् की तरह चलते हैं। कुछ का वर्तमान अङ्ग विशेष है: गम् → गच्छ, पा → पिब, दृश् → पश्य, स्था → तिष्ठ।', 'Most roots behave like पठ्. A few have a special present stem: गम् → गच्छ, पा → पिब, दृश् → पश्य, स्था → तिष्ठ.') },
            { kind: 'verbTable', title: bi('गम् (जाना), लट् लकार', 'गम् (to go), लट्'), dhatuId: 'gam', lakara: 'lat' },
            {
                kind: 'teach',
                title: bi('तालिका पढ़ना', 'Reading the table'),
                beats: [
                    beat(bi('गच्छति, गच्छतः, गच्छन्ति: वह जाता है, वे दोनों, वे सब।', 'गच्छति, गच्छतः, गच्छन्ति: he goes, the two go, they all go.'), [line('गच्छ|saffron:ति|  गच्छ|saffron:तः|  गच्छ|saffron:न्ति', 'lg')]),
                    beat(bi('गच्छामि, गच्छावः, गच्छामः: म और व से पहले लम्बा आ।', 'गच्छामि, गच्छावः, गच्छामः: long आ before म and व.'), [line('ग|terracotta:च्छामि|  ग|terracotta:च्छावः|  ग|terracotta:च्छामः', 'lg')])
                ]
            },
            { kind: 'grid', title: bi('गम्: किसी भी खाने को छुइए', 'गम्: tap any cell'), mode: 'verb', dhatuId: 'gam' },
            fill(bi('हम सब विद्यालय जाते हैं।', 'We all go to school.'), 'वयं विद्यालयं ', '।', ['गच्छामः', 'गच्छावः', 'गच्छन्ति'], 'गच्छामः', bi('वयम् = उत्तम पुरुष बहुवचन: गच्छामः।', 'वयम् = उत्तम पुरुष plural: गच्छामः.'), 'verb-production', 'verb:gam:lat:P:uttama:bahu'),
            fill(bi('हम दोनों गाँव जाते हैं।', 'We two go to the village.'), 'आवां ग्रामं ', '।', ['गच्छावः', 'गच्छामः', 'गच्छतः'], 'गच्छावः', bi('आवाम् = उत्तम पुरुष द्विवचन: गच्छावः।', 'आवाम् = उत्तम पुरुष dual: गच्छावः.'), 'verb-production', 'verb:gam:lat:P:uttama:dvi'),
            mcq(bi('"वे दोनों पीते हैं" के लिए पा (पिब) का रूप?', '"The two of them drink": which form of पा (पिब)?'), ['पिबतः', 'पिबन्ति', 'पिबति'], 0, bi('प्रथम पुरुष द्विवचन: पिबतः।', 'प्रथम पुरुष dual: पिबतः.'), 'conjugation', { item: 'verb:pa:lat:P:prathama:dvi' }),
            build(bi('बनाइए: "हम सब चाँद देखते हैं।"', 'Build: "We all see the moon."'), ['वयं', 'चन्द्रं', 'पश्यामः', 'पश्यन्ति', 'पश्यावः'], ['वयं', 'चन्द्रं', 'पश्यामः'], 'sentence', { orderFree: true, item: 'verb:drs:lat:P:uttama:bahu' }),
            build(bi('बनाइए: "तुम दोनों यहाँ ठहरो।" (वर्तमान: तुम दोनों यहाँ ठहरते हो)', 'Build: "You two stay here."'), ['युवाम्', 'अत्र', 'तिष्ठथः', 'तिष्ठथ', 'तिष्ठतः'], ['युवाम्', 'अत्र', 'तिष्ठथः'], 'sentence', { orderFree: true, item: 'verb:stha:lat:P:madhyama:dvi' }),
            {
                kind: 'match',
                title: bi('कर्ता और क्रिया', 'Subject and verb'),
                pairs: [
                    { left: 'बालकाः', right: 'गच्छन्ति' },
                    { left: 'बालकौ', right: 'गच्छतः' },
                    { left: 'बालकः', right: 'गच्छति' }
                ],
                skill: 'conjugation'
            }
        ]
    },
    {
        id: 'l18-linga',
        number: 18,
        level: 4,
        moduleId: 'm4-vibhakti',
        title: bi('लिङ्ग', 'Grammatical gender'),
        sa: 'लिङ्गम्',
        summary: bi('पुंलिङ्ग, स्त्रीलिङ्ग, नपुंसकलिङ्ग।', 'Masculine, feminine and neuter.'),
        minutes: 6,
        skills: ['declension', 'vocabulary'],
        steps: [
            { kind: 'intro', title: bi('हर संज्ञा का एक लिङ्ग', 'Every noun has a gender'), body: bi('हर संस्कृत संज्ञा का व्याकरणिक लिङ्ग होता है। यह शब्द का गुण है; यह हमेशा पुरुष या स्त्री होने से तय नहीं होता।', 'Every Sanskrit noun has a grammatical gender. It belongs to the word, and is not always about being male or female.') },
            {
                kind: 'teach',
                title: bi('तीन लिङ्ग', 'Three genders'),
                beats: [
                    beat(bi('पुंलिङ्ग: रामः, वृक्षः, बालकः (प्रायः -अः)।', 'Masculine: रामः, वृक्षः, बालकः (often -अः).'), [line('saffron:रामः  वृक्षः  बालकः', 'lg', bi('पुंलिङ्ग', 'masculine'))]),
                    beat(bi('स्त्रीलिङ्ग: लता, नदी, माता (प्रायः -आ, -ई)।', 'Feminine: लता, नदी, माता (often -आ, -ई).'), [line('terracotta:लता  नदी  माता', 'lg', bi('स्त्रीलिङ्ग', 'feminine'))]),
                    beat(bi('नपुंसकलिङ्ग: फलम्, पुस्तकम्, जलम् (प्रायः -अम्)।', 'Neuter: फलम्, पुस्तकम्, जलम् (often -अम्).'), [line('green:फलम्  पुस्तकम्  जलम्', 'lg', bi('नपुंसकलिङ्ग', 'neuter'))]),
                    beat(bi('पर अर्थ से लिङ्ग तय नहीं होता: मित्रम् (दोस्त) नपुंसकलिङ्ग है!', 'But meaning does not decide gender: मित्रम् (friend) is neuter!'), [line('मित्रम्  =  नपुंसक', 'lg')])
                ]
            },
            mcq(bi('लता का लिङ्ग?', 'Gender of लता?'), [bi('स्त्रीलिङ्ग', 'feminine'), bi('पुंलिङ्ग', 'masculine'), bi('नपुंसकलिङ्ग', 'neuter')], 0, bi('लता आकारान्त स्त्रीलिङ्ग है।', 'लता is a feminine noun in -ā.'), 'declension'),
            mcq(bi('फलम् का लिङ्ग?', 'Gender of फलम्?'), [bi('नपुंसकलिङ्ग', 'neuter'), bi('पुंलिङ्ग', 'masculine'), bi('स्त्रीलिङ्ग', 'feminine')], 0, bi('फलम् अकारान्त नपुंसकलिङ्ग है।', 'फलम् is a neuter noun in -a.'), 'declension'),
            mcq(bi('वृक्षः का लिङ्ग?', 'Gender of वृक्षः?'), [bi('पुंलिङ्ग', 'masculine'), bi('स्त्रीलिङ्ग', 'feminine'), bi('नपुंसकलिङ्ग', 'neuter')], 0, bi('वृक्षः अकारान्त पुंलिङ्ग है।', 'वृक्षः is a masculine noun in -a.'), 'declension'),
            {
                kind: 'match',
                title: bi('शब्द और लिङ्ग', 'Word and gender'),
                pairs: [
                    { left: 'रामः', right: bi('पुंलिङ्ग', 'masculine') },
                    { left: 'लता', right: bi('स्त्रीलिङ्ग', 'feminine') },
                    { left: 'फलम्', right: bi('नपुंसकलिङ्ग', 'neuter') }
                ],
                skill: 'declension'
            },
            mcq(bi('नदी के स्थान पर कौन सा सर्वनाम?', 'Which pronoun stands for नदी (river)?'), ['सा', 'सः', 'तत्'], 0, bi('नदी स्त्रीलिङ्ग है, इसलिए सा।', 'नदी is feminine, so सा.'), 'declension'),
            {
                kind: 'teach',
                title: bi('तीनों लिङ्ग, तीनों वचन', 'Three genders, three numbers'),
                beats: [
                    beat(bi('रामः रामौ रामाः', 'रामः रामौ रामाः'), [line('रामः  रामौ  रामाः', 'lg', bi('पुंलिङ्ग', 'masculine'))], 'रामः रामौ रामाः'),
                    beat(bi('लता लते लताः', 'लता लते लताः'), [line('लता  लते  लताः', 'lg', bi('स्त्रीलिङ्ग', 'feminine'))], 'लता लते लताः'),
                    beat(bi('फलम् फले फलानि: ये प्रथमा विभक्ति के तीनों वचन हैं।', 'फलम् फले फलानि: the three numbers of the first case (प्रथमा).'), [line('फलम्  फले  फलानि', 'lg', bi('नपुंसकलिङ्ग', 'neuter'))], 'फलम् फले फलानि')
                ]
            }
        ]
    },
    {
        id: 'l19-prathama-dvitiya',
        number: 19,
        level: 4,
        moduleId: 'm4-vibhakti',
        title: bi('कर्ता और कर्म', 'Subject and object'),
        sa: 'प्रथमा द्वितीया च',
        summary: bi('कौन करता है (प्रथमा) और किस पर (द्वितीया)।', 'Who does it (प्रथमा) and to what (द्वितीया).'),
        minutes: 7,
        skills: ['vibhakti', 'declension', 'sentence'],
        steps: [
            { kind: 'intro', title: bi('शब्द का अन्त उसकी भूमिका बताता है', 'The ending tells the role'), body: bi('कौन करता है? → प्रथमा (कर्ता)। किस पर काम होता है? → द्वितीया (कर्म)।', 'Who does it? → प्रथमा (subject). What is it done to? → द्वितीया (object).') },
            {
                kind: 'teach',
                title: bi('कर्ता और कर्म', 'Subject and object'),
                beats: [
                    beat(bi('रामः पुस्तकं पठति। रामः = कर्ता (प्रथमा), पुस्तकम् = कर्म (द्वितीया)।', 'रामः पुस्तकं पठति. रामः is the subject (प्रथमा); पुस्तकम् is the object (द्वितीया).'), [line('saffron:रामः| |terracotta:पुस्तकं| पठति।', 'xl')], 'रामः पुस्तकं पठति।'),
                    beat(bi('रामः → रामम् (कर्म के रूप में)। लता → लताम्। फलम् वैसा ही रहता है।', 'As an object रामः becomes रामम्; लता becomes लताम्; फलम् stays फलम्.'), [line('रामः → रा|terracotta:मम्', 'lg'), line('लता → ल|terracotta:ताम्', 'lg')]),
                    beat(bi('बालिका रामं पश्यति। बालिका राम को देखती है।', 'बालिका रामं पश्यति. The girl sees Rāma.'), [line('बालिका |terracotta:रामं| पश्यति।', 'xl')], 'बालिका रामं पश्यति।'),
                    beat(bi('क्रम बदल सकता है: पुस्तकं रामः पठति। अर्थ वही, क्योंकि अन्त भूमिका बताते हैं।', 'The order can change: पुस्तकं रामः पठति still means "Rāma reads the book", because the endings show the roles.'), [line('terracotta:पुस्तकं| |saffron:रामः| पठति।', 'lg')])
                ]
            },
            { kind: 'nounTable', title: bi('राम: प्रथमा और द्वितीया', 'राम: first and second cases'), paradigmId: 'rama', rows: [1, 2] },
            mcq(bi('"बालकः फलं खादति" में कर्म कौन है?', 'In बालकः फलं खादति, which word is the object (कर्म)?'), ['फलम्', 'बालकः', 'खादति'], 0, bi('क्या खाता है? फलम्: कर्म, द्वितीया।', 'Eats what? फलम्: the object, in द्वितीया.'), 'vibhakti', { item: 'noun:phala:2:eka' }),
            mcq(bi('कर्म के रूप में "राम"?', 'Rāma as the object:'), ['रामम्', 'रामः', 'रामाः'], 0, bi('द्वितीया एकवचन: रामम्।', 'द्वितीया singular: रामम्.'), 'declension', { item: 'noun:rama:2:eka' }),
            build(bi('बनाइए: "बालिका राम को देखती है।"', 'Build: "The girl sees Rāma."'), ['बालिका', 'रामं', 'पश्यति', 'रामः', 'पश्यामि'], ['बालिका', 'रामं', 'पश्यति'], 'sentence', { orderFree: true }),
            build(bi('बनाइए: "बालक बालिका को देखता है।"', 'Build: "The boy sees the girl."'), ['बालकः', 'बालिकां', 'पश्यति', 'बालिका', 'पश्यसि'], ['बालकः', 'बालिकां', 'पश्यति'], 'sentence', { orderFree: true }),
            fill(bi('वह गाँव जाता है।', 'He goes to the village.'), 'सः ', ' गच्छति।', ['ग्रामं', 'ग्रामः', 'ग्रामे'], 'ग्रामं', bi('जाने का स्थान द्वितीया में: ग्रामं गच्छति।', 'The destination takes द्वितीया: ग्रामं गच्छति.'), 'vibhakti'),
            mcq(bi('"पुस्तकं रामः पठति" का अर्थ?', 'What does पुस्तकं रामः पठति mean?'), [bi('राम पुस्तक पढ़ता है', 'Rāma reads the book'), bi('पुस्तक राम को पढ़ती है', 'The book reads Rāma'), bi('यह गलत वाक्य है', 'It is not a correct sentence')], 0, bi('रामः प्रथमा (कर्ता) है, चाहे वह कहीं भी आए।', 'रामः is in प्रथमा (the subject) wherever it stands.'), 'vibhakti')
        ]
    },
    {
        id: 'l20-first-story',
        number: 20,
        level: 4,
        moduleId: 'm4-vibhakti',
        assessment: true,
        passMark: 0.7,
        title: bi('पहली कहानी और पुनरावृत्ति', 'First story and review'),
        sa: 'प्रथमा कथा',
        summary: bi('एक पूरी कहानी पढ़ें, फिर अब तक का अभ्यास।', 'Read a whole story, then review everything so far.'),
        minutes: 8,
        skills: ['reading', 'comprehension', 'conjugation', 'declension'],
        complete: { sa: 'साधु! साधु!', text: bi('आपने पहला स्तर पूरा किया और अपनी पहली संस्कृत कहानी पढ़ी।', 'You have finished the first stage and read your first Sanskrit story.') },
        steps: [
            { kind: 'intro', art: 'guru', title: bi('अब एक असली कहानी', 'Now a real story'), body: bi('जो सीखा है, उससे एक पूरी कहानी पढ़िए। किसी भी शब्द को छूकर उसका अर्थ देखिए।', 'Use everything you have learned to read a whole story. Tap any word for its meaning.') },
            { kind: 'story', storyId: 'rama-reads' },
            mcq(bi('राम क्या पीता है?', 'What does Rāma drink?'), ['जलम्', 'दुग्धम्', 'फलम्'], 0, bi('सः जलं पिबति।', 'सः जलं पिबति: he drinks water.'), 'comprehension'),
            mcq(bi('राम कहाँ जाता है?', 'Where does Rāma go?'), ['विद्यालयम्', 'उद्यानम्', 'ग्रामम्'], 0, bi('सः विद्यालयं गच्छति।', 'सः विद्यालयं गच्छति: to school.'), 'comprehension'),
            { kind: 'reorder', prompt: bi('कहानी के वाक्यों को सही क्रम में लगाइए', 'Put the story’s sentences in order'), items: ['रामः बालकः अस्ति।', 'सः प्रातः उत्तिष्ठति।', 'सः जलं पिबति।', 'रामः पुस्तकं पठति।', 'सः विद्यालयं गच्छति।'], skill: 'comprehension' },
            fill(bi('पुनरावृत्ति: मैं पढ़ता हूँ।', 'Review: I read.'), 'अहं ', '।', ['पठामि', 'पठति', 'पठामः'], 'पठामि', bi('अहम् → पठामि।', 'अहम् → पठामि.'), 'verb-production', 'verb:path:lat:P:uttama:eka'),
            mcq(bi('पुनरावृत्ति: दो बालक = ?', 'Review: two boys = ?'), ['बालकौ', 'बालकाः', 'बालकः'], 0, bi('द्विवचन: बालकौ।', 'Dual: बालकौ.'), 'declension', { item: 'concept:dvivacana' }),
            build(bi('पुनरावृत्ति: "वह बालिका है।"', 'Review: "She is a girl."'), ['सा', 'बालिका', 'सः', 'बालकः'], ['सा', 'बालिका']),
            mcq(bi('पुनरावृत्ति: त्वम् किस पुरुष में?', 'Review: which पुरुष is त्वम्?'), ['मध्यम', 'उत्तम', 'प्रथम'], 0, bi('जिससे बात हो रही है: मध्यम पुरुष।', 'The one spoken to: मध्यम पुरुष.'), 'conjugation', { item: 'concept:madhyama' }),
            mcq(bi('पुनरावृत्ति: पुस्तकम् का लिङ्ग?', 'Review: gender of पुस्तकम्?'), [bi('नपुंसकलिङ्ग', 'neuter'), bi('पुंलिङ्ग', 'masculine'), bi('स्त्रीलिङ्ग', 'feminine')], 0, bi('-म् वाला अकारान्त नपुंसक।', 'A neuter noun in -a (ending -म्).'), 'declension'),
            build(bi('पुनरावृत्ति: "वे सब गाँव जाते हैं।"', 'Review: "They all go to the village."'), ['ते', 'ग्रामं', 'गच्छन्ति', 'गच्छति', 'ग्रामः'], ['ते', 'ग्रामं', 'गच्छन्ति'], 'sentence', { orderFree: true })
        ]
    },
    {
        id: 'l21-sankhya',
        number: 21,
        level: 5,
        moduleId: 'm5-everyday',
        title: bi('गिनती', 'Numbers 1 to 10'),
        sa: 'सङ्ख्याः',
        summary: bi('एक से दस तक, और लिङ्ग के साथ बदलने वाली संख्याएँ।', 'One to ten, and the numbers that change with gender.'),
        minutes: 6,
        skills: ['vocabulary', 'listening', 'declension'],
        vocab: ['n1', 'n2', 'n3', 'n4', 'n5', 'n6', 'n7', 'n8', 'n9', 'n10'],
        steps: [
            { kind: 'intro', title: bi('एकम्, द्वे, त्रीणि...', 'एकम्, द्वे, त्रीणि…'), body: bi('गिनती करते समय संस्कृत में ये रूप बोले जाते हैं। देवनागरी अंक भी देखिए।', 'These are the forms used when counting aloud. Notice the Devanagari numerals too.') },
            {
                kind: 'teach',
                title: bi('एक से दस', 'One to ten'),
                beats: [
                    beat(bi('१ एकम्, २ द्वे, ३ त्रीणि, ४ चत्वारि, ५ पञ्च', '1 एकम्, 2 द्वे, 3 त्रीणि, 4 चत्वारि, 5 पञ्च'), [line('१ एकम्   २ द्वे   ३ त्रीणि', 'lg'), line('४ चत्वारि   ५ पञ्च', 'lg')], 'एकम् द्वे त्रीणि चत्वारि पञ्च'),
                    beat(bi('६ षट्, ७ सप्त, ८ अष्ट, ९ नव, १० दश', '6 षट्, 7 सप्त, 8 अष्ट, 9 नव, 10 दश'), [line('६ षट्   ७ सप्त   ८ अष्ट', 'lg'), line('९ नव   १० दश', 'lg')], 'षट् सप्त अष्ट नव दश'),
                    beat(bi('एक से चार तक की संख्याएँ लिङ्ग के साथ बदलती हैं: एकः बालकः, एका बालिका, एकं फलम्।', 'One to four change with gender: एकः बालकः, एका बालिका, एकं फलम्.'), [line('saffron:एकः बालकः', 'md'), line('terracotta:एका बालिका', 'md'), line('green:एकं फलम्', 'md')]),
                    beat(bi('द्वौ बालकौ, द्वे बालिके, द्वे फले: "दो" के साथ संज्ञा द्विवचन में।', 'द्वौ बालकौ, द्वे बालिके, द्वे फले: with "two", the noun is dual.'), [line('saffron:द्वौ बालकौ', 'md'), line('terracotta:द्वे बालिके', 'md')])
                ]
            },
            {
                kind: 'match',
                title: bi('अंक और शब्द', 'Numerals and words'),
                pairs: [
                    { left: '१', right: 'एकम्' },
                    { left: '२', right: 'द्वे' },
                    { left: '३', right: 'त्रीणि' },
                    { left: '४', right: 'चत्वारि' },
                    { left: '५', right: 'पञ्च' }
                ],
                skill: 'vocabulary'
            },
            mcq(bi('सप्त = ?', 'सप्त = ?'), ['7', '6', '9'], 0, bi('सप्त = सात।', 'सप्त is seven.'), 'vocabulary'),
            mcq(bi('"दो बालक" में सही संख्या-शब्द?', 'The right number word in "two boys"?'), ['द्वौ', 'द्वे', 'त्रयः'], 0, bi('पुंलिङ्ग "दो": द्वौ बालकौ।', 'Masculine "two": द्वौ बालकौ.'), 'declension'),
            listen('पञ्च', ['पञ्च', 'षट्', 'सप्त']),
            fill(bi('एक बालिका', 'One girl'), 'एका ', '', ['बालिका', 'बालकः', 'फलम्'], 'बालिका', bi('एका (स्त्रीलिङ्ग) के साथ बालिका।', 'एका (feminine) goes with बालिका.'), 'declension')
        ]
    },
    {
        id: 'l22-prashna',
        number: 22,
        level: 5,
        moduleId: 'm5-everyday',
        title: bi('प्रश्न पूछना', 'Asking questions'),
        sa: 'प्रश्नाः',
        summary: bi('कः, किम्, कुत्र, कदा, कथम् और नकार (न)।', 'कः, किम्, कुत्र, कदा, कथम् and negation with न.'),
        minutes: 6,
        skills: ['sentence', 'vocabulary'],
        vocab: ['kah', 'kim', 'kutra', 'kada', 'katham', 'kimartham', 'na'],
        steps: [
            { kind: 'intro', title: bi('कौन? क्या? कहाँ?', 'Who? What? Where?'), body: bi('प्रश्नवाचक शब्द वाक्य में कहीं भी आ सकते हैं; प्रायः क्रिया से पहले आते हैं।', 'Question words can stand anywhere in the sentence; they often come before the verb.') },
            {
                kind: 'teach',
                title: bi('प्रश्न और नकार', 'Questions and "not"'),
                beats: [
                    beat(bi('सः कः? वह कौन है?', 'सः कः? Who is he?'), [line('सः |saffron:कः?', 'xl')], 'सः कः?'),
                    beat(bi('त्वं किं पठसि? तुम क्या पढ़ते हो?', 'त्वं किं पठसि? What do you read?'), [line('त्वं |saffron:किं| पठसि?', 'xl')], 'त्वं किं पठसि?'),
                    beat(bi('त्वं कुत्र गच्छसि? तुम कहाँ जाते हो?', 'त्वं कुत्र गच्छसि? Where are you going?'), [line('त्वं |saffron:कुत्र| गच्छसि?', 'xl')], 'त्वं कुत्र गच्छसि?'),
                    beat(bi('नकार के लिए क्रिया से पहले "न": अहं न जानामि। मैं नहीं जानता।', 'For "not", put न before the verb: अहं न जानामि. I do not know.'), [line('अहं |terracotta:न| जानामि।', 'xl')], 'अहं न जानामि।')
                ]
            },
            {
                kind: 'match',
                title: bi('प्रश्नवाचक शब्द', 'Question words'),
                pairs: [
                    { left: 'कुत्र', right: bi('कहाँ', 'where') },
                    { left: 'कदा', right: bi('कब', 'when') },
                    { left: 'कथम्', right: bi('कैसे', 'how') },
                    { left: 'किमर्थम्', right: bi('क्यों', 'why') },
                    { left: 'किम्', right: bi('क्या', 'what') }
                ],
                skill: 'vocabulary'
            },
            mcq(bi('"तुम कहाँ जा रहे हो?"', '"Where are you going?"'), ['त्वं कुत्र गच्छसि?', 'त्वं कदा गच्छसि?', 'त्वं किं गच्छसि?'], 0, bi('कुत्र = कहाँ।', 'कुत्र means where.'), 'sentence'),
            build(bi('बनाइए: "मैं नहीं जानता।"', 'Build: "I do not know."'), ['अहं', 'न', 'जानामि', 'जानाति'], ['अहं', 'न', 'जानामि'], 'sentence', { orderFree: false, hint: bi('न को क्रिया से ठीक पहले रखिए।', 'Put न right before the verb.') }),
            fill(bi('आप कैसे हैं?', 'How are you?'), 'भवान् ', ' अस्ति?', ['कथम्', 'कुत्र', 'कदा'], 'कथम्', bi('कथम् = कैसे।', 'कथम् means how.'), 'vocabulary'),
            build(bi('बनाइए: "तुम क्या देखते हो?"', 'Build: "What do you see?"'), ['त्वं', 'किं', 'पश्यसि', 'पश्यति', 'कुत्र'], ['त्वं', 'किं', 'पश्यसि'], 'sentence', { orderFree: true })
        ]
    }
]

export const LESSON_ORDER: string[] = LESSONS.map(l => l.id)

const LESSON_BY_ID = new Map(LESSONS.map(l => [l.id, l]))

export const getLesson = (id: string): Lesson | undefined => LESSON_BY_ID.get(id)

export const CURRICULUM: CurriculumLevel[] = [
    {
        level: 0,
        sa: 'संस्कृतपरिचयः',
        title: bi('संस्कृत परिचय', 'Sanskrit from zero'),
        summary: bi('भाषा, लिपि, स्वर, व्यञ्जन, मात्राएँ और पहले शब्द।', 'The language, the script, vowels, consonants, vowel signs and first words.'),
        status: 'ready',
        modules: [
            { id: 'm0-intro', sa: 'परिचयः', title: bi('परिचय', 'Introduction'), lessons: ['l01-intro'] },
            { id: 'm0-lipi', sa: 'देवनागरी', title: bi('देवनागरी लिपि', 'The Devanagari script'), lessons: ['l02-svara', 'l03-vyanjana', 'l04-matra', 'l05-pathanam'] }
        ]
    },
    {
        level: 1,
        sa: 'प्रथमाः शब्दाः',
        title: bi('पहले शब्द', 'First words'),
        summary: bi('अभिवादन, वस्तुएँ और सर्वनाम।', 'Greetings, objects and pronouns.'),
        status: 'ready',
        modules: [
            { id: 'm1-words', sa: 'शब्दाः', title: bi('शब्द', 'Words'), lessons: ['l06-greetings', 'l07-objects'] },
            { id: 'm1-pronouns', sa: 'सर्वनामानि', title: bi('सर्वनाम', 'Pronouns'), lessons: ['l08-aham-tvam', 'l09-sah-sa-tat'] }
        ]
    },
    {
        level: 2,
        sa: 'वचनं पुरुषः च',
        title: bi('वचन और पुरुष', 'Number and person'),
        summary: bi('एकवचन, द्विवचन, बहुवचन; उत्तम, मध्यम, प्रथम पुरुष।', 'Singular, dual and plural; the three persons.'),
        status: 'ready',
        modules: [
            { id: 'm2-vacana', sa: 'वचनम्', title: bi('वचन', 'Number'), lessons: ['l10-ekavacana', 'l11-dvivacana', 'l12-bahuvacana'] },
            { id: 'm2-purusha', sa: 'पुरुषः', title: bi('पुरुष', 'Person'), lessons: ['l13-purusha'] }
        ]
    },
    {
        level: 3,
        sa: 'धातुः लट् च',
        title: bi('धातु और वर्तमान काल', 'Roots and the present tense'),
        summary: bi('धातु, लट् लकार और पहले वाक्य।', 'Verb roots, the present tense and first sentences.'),
        status: 'ready',
        modules: [{ id: 'm3-dhatu', sa: 'धातवः', title: bi('धातु', 'Verb roots'), lessons: ['l14-dhatu', 'l15-first-sentence', 'l16-simple-sentences', 'l17-more-lat'] }]
    },
    {
        level: 4,
        sa: 'लिङ्गं विभक्तिः च',
        title: bi('लिङ्ग और विभक्ति', 'Gender and case'),
        summary: bi('तीन लिङ्ग, कर्ता और कर्म, और पहली कहानी।', 'Three genders, subject and object, and your first story.'),
        status: 'ready',
        modules: [{ id: 'm4-vibhakti', sa: 'विभक्तिः', title: bi('विभक्ति', 'Case'), lessons: ['l18-linga', 'l19-prathama-dvitiya', 'l20-first-story'] }]
    },
    {
        level: 5,
        sa: 'दैनिकसंस्कृतम्',
        title: bi('रोज़ की संस्कृत', 'Everyday Sanskrit'),
        summary: bi('गिनती और प्रश्न।', 'Numbers and questions.'),
        status: 'ready',
        modules: [{ id: 'm5-everyday', sa: 'व्यवहारः', title: bi('व्यवहार', 'Everyday use'), lessons: ['l21-sankhya', 'l22-prashna'] }]
    },
    {
        level: 6,
        sa: 'लकाराः',
        title: bi('पाँच मुख्य लकार', 'The five core lakāras'),
        summary: bi('लृट्, लङ्, लोट्, विधिलिङ्: भविष्य, भूत, आज्ञा, उपदेश।', 'लृट्, लङ्, लोट्, विधिलिङ्: future, past, commands and advice.'),
        status: 'preparing',
        modules: [],
        labLinks: [
            { href: '/sanskrit/grammar/lakara', label: bi('लकार प्रयोगशाला खोलें', 'Open the Lakāra overview') },
            { href: '/sanskrit/grammar/dhatu', label: bi('धातुरूप प्रयोगशाला', 'Open the Dhātu lab') }
        ]
    },
    {
        level: 7,
        sa: 'सर्वाः विभक्तयः',
        title: bi('सभी विभक्तियाँ और कारक', 'All cases and kārakas'),
        summary: bi('तृतीया से सम्बोधन तक, और वाक्य-विश्लेषण।', 'The instrumental through the vocative, and sentence analysis.'),
        status: 'preparing',
        modules: [],
        labLinks: [
            { href: '/sanskrit/grammar/vibhakti', label: bi('विभक्ति प्रयोगशाला', 'Open the Vibhakti explorer') },
            { href: '/sanskrit/grammar/shabdarupa', label: bi('शब्दरूप प्रयोगशाला', 'Open the Shabdarūpa lab') },
            { href: '/sanskrit/grammar/karaka', label: bi('कारक और वाक्य-आरेख', 'Open Kāraka diagrams') }
        ]
    },
    {
        level: 8,
        sa: 'सन्धिः समासः च',
        title: bi('सन्धि और समास', 'Sandhi and compounds'),
        summary: bi('स्वर, व्यञ्जन, विसर्ग सन्धि; छह प्रकार के समास।', 'Vowel, consonant and visarga sandhi; the six kinds of compound.'),
        status: 'preparing',
        modules: [],
        labLinks: [
            { href: '/sanskrit/grammar/sandhi', label: bi('सन्धि प्रयोगशाला', 'Open the Sandhi lab') },
            { href: '/sanskrit/grammar/samasa', label: bi('समास प्रयोगशाला', 'Open the Samāsa lab') }
        ]
    },
    {
        level: 9,
        sa: 'कृदन्तं तद्धितं च',
        title: bi('कृदन्त, तद्धित, उपसर्ग', 'Participles, derivatives and prefixes'),
        summary: bi('क्त, क्तवतु, शतृ, तुमुन्, क्त्वा, ल्यप्... और उपसर्ग।', 'क्त, क्तवतु, शतृ, तुमुन्, क्त्वा, ल्यप्… and verbal prefixes.'),
        status: 'preparing',
        modules: [],
        labLinks: [{ href: '/sanskrit/grammar/pratyaya', label: bi('प्रत्यय प्रयोगशाला', 'Open the Pratyaya lab') }]
    },
    {
        level: 10,
        sa: 'साहित्यपठनम्',
        title: bi('ग्रन्थ पठन', 'Reading classical texts'),
        summary: bi('सुभाषित, कथाएँ और व्याकरण-सहायता के साथ मूल ग्रन्थ।', 'Proverbs, tales and original texts with grammatical help.'),
        status: 'preparing',
        modules: [],
        labLinks: [{ href: '/sanskrit/stories', label: bi('कथाएँ पढ़ें', 'Read the stories') }]
    }
]
